/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  LinearProgress,
  Link,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Close as CloseIcon,
  AutoAwesome as AIIcon,
  TextFields as OCRIcon,
  Summarize as SummarizeIcon,
  Translate as TranslateIcon,
  QuestionAnswer as QAIcon,
  Search as SearchIcon,
  Analytics as AnalyticsIcon,
  Description as DocumentIcon,
  Image as ImageIcon,
  TableChart as TableIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  ArrowForward as ArrowIcon,
  Maximize as MaximizeIcon,
  Code as CodeIcon,
  Apps as AppsIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  TrendingUp as TrendingUpIcon,
  Palette as PaletteIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Sports as SportsIcon,
  EmojiEmotions as EmojiIcon,
  Category as CategoryIcon,
  Star as StarIcon,
  Speed as SpeedIcon,
  Psychology as SkillAnalyzeIcon,
  School as SchoolIcon,
  Language as LanguageIcon,
  Work as WorkIcon,
  Build as BuildIcon,
  Psychology as SoftSkillsIcon,
  Badge as BadgeIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { ApiKeysService } from '../../services/apiKeysService';
import { supabase } from '../../lib/supabase';
import ReactMarkdown from 'react-markdown';
import parse from 'html-react-parser';
import { toast } from 'react-hot-toast';
import AIApplicationWriterModal from './AIApplicationWriterModal';
import FillGapsExerciseModal from '../Exercises/FillGapsExerciseModal';
import MatchingExerciseModal from '../Exercises/MatchingExerciseModal';
import MultipleChoiceExerciseModal from '../Exercises/MultipleChoiceExerciseModal';

interface UserDocument {
  id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  document_type: string;
  description?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  url?: string;
}

interface AIDocumentProcessingModalProps {
  open: boolean;
  onClose: () => void;
  document: UserDocument | null;
  autoProcessImageTags?: boolean; // New prop to auto-trigger image tags
}

interface AIOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: 'extraction' | 'analysis' | 'transformation' | 'development' | 'education';
  comingSoon?: boolean;
  premium?: boolean;
}

// Function to check if document is an image type
const isImageDocument = (fileType: string): boolean => {
  const imageTypes = ['png', 'jpeg', 'jpg', 'gif', 'tiff', 'bmp', 'webp', 'svg'];
  return imageTypes.some(type => fileType.toLowerCase().includes(type));
};

// Function to get AI options based on context - updated to be dynamic
const getAIOptions = (isCompanyMode: boolean, document?: UserDocument): AIOption[] => {
  // If document is an image, show both Image Analysis and Image Tags
  if (document && isImageDocument(document.file_type)) {
    return [
      {
        id: 'image-analysis',
        title: 'Image Analysis',
        description: 'Analyze images to extract objects, text, faces, and other visual elements using AI',
        icon: <ImageIcon />,
        category: 'analysis',
      },
      {
        id: 'image-tags',
        title: 'Image Tags',
        description: 'Generate relevant tags and keywords to categorize and organize your images',
        icon: <AnalyticsIcon />,
        category: 'analysis',
      },
    ];
  }

  // Default options for non-image documents
  return [
  {
    id: 'ocr',
    title: 'Text Extraction',
    description: 'Extract text from images, scanned documents, and PDFs with high accuracy',
    icon: <OCRIcon />,
    category: 'extraction',
  },
  {
    id: 'summarize',
    title: isCompanyMode ? 'Smart Summarization' : 'Smart Summarization (User)',
    description: 'Generate concise summaries of long documents, contracts, and reports',
    icon: <SummarizeIcon />,
    category: 'analysis',
  },
  {
    id: 'test',
    title: 'Test Edge Function',
    description: 'Test the new mistral-summarize-new edge function',
    icon: <CheckIcon />,
    category: 'development',
  },
  {
    id: 'translate',
    title: 'Document Translation',
    description: 'Translate documents to multiple languages while preserving formatting',
    icon: <TranslateIcon />,
    category: 'transformation',
  },
  {
    id: 'qa',
    title: 'Document Q&A',
    description: 'Ask questions about your document content and get intelligent answers',
    icon: <QAIcon />,
    category: 'analysis',
  },
  {
    id: 'search',
    title: 'Semantic Search',
    description: 'Find specific information across multiple documents using natural language',
    icon: <SearchIcon />,
    category: 'analysis',
  },
  {
    id: 'analytics',
    title: 'Content Analytics',
    description: 'Analyze document structure, sentiment, and extract key insights',
    icon: <AnalyticsIcon />,
    category: 'analysis',
  },
  {
    id: 'skill-analyze',
    title: 'Skill Analyze',
    description: 'Analyze and extract skills from documents using AI-powered analysis',
    icon: <SkillAnalyzeIcon />,
    category: 'analysis',
  },
  {
    id: 'table-extraction',
    title: 'Table Extraction',
    description: 'Extract tables and structured data from documents and images',
    icon: <TableIcon />,
    category: 'extraction',
  },
  {
    id: 'write-ai-app',
    title: 'Write AI Application',
    description: 'Create custom AI applications using your documents and data',
    icon: <CodeIcon />,
    category: 'development',
  },
  {
    id: 'generate-exercise',
    title: 'Generate Exercise',
    description: 'Create interactive learning exercises and quizzes based on document content',
    icon: <SchoolIcon />,
    category: 'education',
  },
  ];
};

const CATEGORY_COLORS: Record<string, string> = {
  extraction: '#3b82f6',
  analysis: '#10b981',
  transformation: '#f59e0b',
  development: '#8b5cf6',
  education: '#ec4899',
};

const CATEGORY_LABELS: Record<string, string> = {
  extraction: 'Text Extraction',
  analysis: 'Content Analysis',
  transformation: 'Document Transformation',
  development: 'AI Development',
  education: 'Learning & Education',
};

const AIDocumentProcessingModal: React.FC<AIDocumentProcessingModalProps> = ({
  open,
  onClose,
  document,
  autoProcessImageTags = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get company context from URL
  const companyId = searchParams.get('company');
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [showAIApplicationWriter, setShowAIApplicationWriter] = useState(false);
  const [isJobAppWriterMinimized, setIsJobAppWriterMinimized] = useState(false);
  const [showExerciseSelection, setShowExerciseSelection] = useState(false);
  const [showFillGapsExercise, setShowFillGapsExercise] = useState(false);
  const [showMatchingExercise, setShowMatchingExercise] = useState(false);
  const [showMultipleChoiceExercise, setShowMultipleChoiceExercise] = useState(false);
  const [existingOcr, setExistingOcr] = useState<string | null>(null);
  const [showOcrConfirmation, setShowOcrConfirmation] = useState(false);
  const [summaryResult, setSummaryResult] = useState<any>(null);
  const [skillAnalyzeResult, setSkillAnalyzeResult] = useState<any>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [ocrResult, setOcrResult] = useState<{
    success: boolean;
    extractedText?: string;
    statusInfo?: string;
    formattingInfo?: {
      headings: Array<{level: number, text: string, page: number}>;
      boldText: Array<{text: string, page: number}>;
      italicText: Array<{text: string, page: number}>;
      lists: Array<{type: 'ordered' | 'unordered', items: string[], page: number}>;
      tables: Array<{rows: string[][], page: number}>;
      links: Array<{text: string, url: string, page: number}>;
    };
    error?: string;
    userMessage?: string;
    model?: string;
    usage?: any;
    documentSize?: number;
    processingTime?: string;
    apiKeyId?: string;
    keysTried?: number;
    details?: string;
    // Image tags specific properties
    isImageTags?: boolean;
    tagsResult?: {
      tags?: string[];
      categories?: {
        people?: string[];
        objects?: string[];
        colors?: string[];
        locations?: string[];
        activities?: string[];
        emotions?: string[];
        other?: string[];
      };
      confidence?: number;
      model?: string;
      apiKeyId?: string;
    };
  } | null>(null);

  // Auto-trigger image tags processing when modal opens with autoProcessImageTags
  useEffect(() => {
    console.log('🤖 AI Modal useEffect triggered:', { open, autoProcessImageTags, document: !!document });
    
    if (open && autoProcessImageTags && document && isImageDocument(document.file_type)) {
      console.log('🎯 Auto-triggering image tags processing...');
      // Set the selected option to image-tags and trigger processing
      setSelectedOption('image-tags');
      // Small delay to ensure the modal is fully rendered
      setTimeout(() => {
        console.log('🚀 Starting processImageTags...');
        processImageTags();
      }, 500);
    }
  }, [open, autoProcessImageTags, document]);

  // Categorize skills into different groups - comprehensive for all job types
  const categorizeSkills = (skills: string[]) => {
    const categories = {
      education: [] as string[],
      languages: [] as string[],
      softSkills: [] as string[],
      technicalSkills: [] as string[],
      industrySkills: [] as string[],
      softwareTools: [] as string[],
      salesMarketing: [] as string[],
      healthcare: [] as string[],
      finance: [] as string[],
      educationTeaching: [] as string[],
      legal: [] as string[],
      operations: [] as string[],
      creative: [] as string[],
      certifications: [] as string[],
      other: [] as string[]
    };

    skills.forEach(skill => {
      const lowerSkill = skill.toLowerCase();
      
      // Education & Academic Qualifications
      if (lowerSkill.includes('master') || lowerSkill.includes('bachelor') || lowerSkill.includes('degree') || 
          lowerSkill.includes('university') || lowerSkill.includes('college') || lowerSkill.includes('gpa') ||
          lowerSkill.includes('phd') || lowerSkill.includes('mba') || lowerSkill.includes('diploma') ||
          lowerSkill.includes('associate') || lowerSkill.includes('certificate') || lowerSkill.includes('academic')) {
        categories.education.push(skill);
      }
      // Languages
      else if (lowerSkill.includes('fluent') || lowerSkill.includes('intermediate') || lowerSkill.includes('basics') ||
               lowerSkill.includes('english') || lowerSkill.includes('french') || lowerSkill.includes('german') ||
               lowerSkill.includes('spanish') || lowerSkill.includes('language') || lowerSkill.includes('bilingual') ||
               lowerSkill.includes('mandarin') || lowerSkill.includes('japanese') || lowerSkill.includes('italian') ||
               lowerSkill.includes('portuguese') || lowerSkill.includes('russian') || lowerSkill.includes('arabic')) {
        categories.languages.push(skill);
      }
      // Soft Skills & Interpersonal
      else if (lowerSkill.includes('management') || lowerSkill.includes('leadership') || lowerSkill.includes('communication') ||
               lowerSkill.includes('teamwork') || lowerSkill.includes('time management') || lowerSkill.includes('critical thinking') ||
               lowerSkill.includes('public relations') || lowerSkill.includes('project management') || lowerSkill.includes('problem solving') ||
               lowerSkill.includes('adaptability') || lowerSkill.includes('creativity') || lowerSkill.includes('negotiation') ||
               lowerSkill.includes('customer service') || lowerSkill.includes('presentation') || lowerSkill.includes('mentoring') ||
               lowerSkill.includes('coaching') || lowerSkill.includes('conflict resolution') || lowerSkill.includes('emotional intelligence')) {
        categories.softSkills.push(skill);
      }
      // Technical Skills (IT, Engineering, etc.)
      else if (lowerSkill.includes('javascript') || lowerSkill.includes('python') || lowerSkill.includes('java') ||
               lowerSkill.includes('programming') || lowerSkill.includes('coding') || lowerSkill.includes('software') ||
               lowerSkill.includes('engineering') || lowerSkill.includes('technical') || lowerSkill.includes('database') ||
               lowerSkill.includes('network') || lowerSkill.includes('security') || lowerSkill.includes('system') ||
               lowerSkill.includes('hardware') || lowerSkill.includes('maintenance') || lowerSkill.includes('repair')) {
        categories.technicalSkills.push(skill);
      }
      // Industry-Specific Skills
      else if (lowerSkill.includes('manufacturing') || lowerSkill.includes('production') || lowerSkill.includes('quality control') ||
               lowerSkill.includes('logistics') || lowerSkill.includes('supply chain') || lowerSkill.includes('inventory') ||
               lowerSkill.includes('construction') || lowerSkill.includes('architecture') || lowerSkill.includes('engineering') ||
               lowerSkill.includes('agriculture') || lowerSkill.includes('farming') || lowerSkill.includes('mining') ||
               lowerSkill.includes('transportation') || lowerSkill.includes('aviation') || lowerSkill.includes('maritime')) {
        categories.industrySkills.push(skill);
      }
      // Software & Digital Tools
      else if (lowerSkill.includes('excel') || lowerSkill.includes('word') || lowerSkill.includes('powerpoint') ||
               lowerSkill.includes('outlook') || lowerSkill.includes('office') || lowerSkill.includes('google') ||
               lowerSkill.includes('salesforce') || lowerSkill.includes('crm') || lowerSkill.includes('erp') ||
               lowerSkill.includes('quickbooks') || lowerSkill.includes('sap') || lowerSkill.includes('oracle') ||
               lowerSkill.includes('adobe') || lowerSkill.includes('photoshop') || lowerSkill.includes('illustrator')) {
        categories.softwareTools.push(skill);
      }
      // Sales & Marketing
      else if (lowerSkill.includes('sales') || lowerSkill.includes('marketing') || lowerSkill.includes('advertising') ||
               lowerSkill.includes('promotion') || lowerSkill.includes('branding') || lowerSkill.includes('social media') ||
               lowerSkill.includes('digital marketing') || lowerSkill.includes('seo') || lowerSkill.includes('ppc') ||
               lowerSkill.includes('content creation') || lowerSkill.includes('copywriting') || lowerSkill.includes('email marketing') ||
               lowerSkill.includes('market research') || lowerSkill.includes('customer acquisition') || lowerSkill.includes('retention')) {
        categories.salesMarketing.push(skill);
      }
      // Healthcare & Medical
      else if (lowerSkill.includes('medical') || lowerSkill.includes('healthcare') || lowerSkill.includes('nursing') ||
               lowerSkill.includes('patient care') || lowerSkill.includes('diagnosis') || lowerSkill.includes('treatment') ||
               lowerSkill.includes('pharmacy') || lowerSkill.includes('therapy') || lowerSkill.includes('counseling') ||
               lowerSkill.includes('surgery') || lowerSkill.includes('emergency') || lowerSkill.includes('rehabilitation') ||
               lowerSkill.includes('mental health') || lowerSkill.includes('clinical') || lowerSkill.includes('hospital')) {
        categories.healthcare.push(skill);
      }
      // Finance & Accounting
      else if (lowerSkill.includes('finance') || lowerSkill.includes('accounting') || lowerSkill.includes('bookkeeping') ||
               lowerSkill.includes('budgeting') || lowerSkill.includes('financial analysis') || lowerSkill.includes('auditing') ||
               lowerSkill.includes('tax') || lowerSkill.includes('investment') || lowerSkill.includes('banking') ||
               lowerSkill.includes('insurance') || lowerSkill.includes('payroll') || lowerSkill.includes('forecasting') ||
               lowerSkill.includes('risk management') || lowerSkill.includes('compliance') || lowerSkill.includes('treasury')) {
        categories.finance.push(skill);
      }
      // Education & Teaching
      else if (lowerSkill.includes('teaching') || lowerSkill.includes('education') || lowerSkill.includes('training') ||
               lowerSkill.includes('instruction') || lowerSkill.includes('curriculum') || lowerSkill.includes('pedagogy') ||
               lowerSkill.includes('tutoring') || lowerSkill.includes('mentoring') || lowerSkill.includes('coaching') ||
               lowerSkill.includes('academic') || lowerSkill.includes('research') || lowerSkill.includes('scholarship') ||
               lowerSkill.includes('student') || lowerSkill.includes('learning') || lowerSkill.includes('assessment')) {
        categories.educationTeaching.push(skill);
      }
      // Legal & Compliance
      else if (lowerSkill.includes('legal') || lowerSkill.includes('law') || lowerSkill.includes('attorney') ||
               lowerSkill.includes('paralegal') || lowerSkill.includes('compliance') || lowerSkill.includes('regulatory') ||
               lowerSkill.includes('contract') || lowerSkill.includes('litigation') || lowerSkill.includes('corporate law') ||
               lowerSkill.includes('criminal') || lowerSkill.includes('civil') || lowerSkill.includes('immigration') ||
               lowerSkill.includes('intellectual property') || lowerSkill.includes('employment law') || lowerSkill.includes('real estate law')) {
        categories.legal.push(skill);
      }
      // Operations & Administration
      else if (lowerSkill.includes('operations') || lowerSkill.includes('administration') || lowerSkill.includes('administrative') ||
               lowerSkill.includes('office management') || lowerSkill.includes('executive assistant') || lowerSkill.includes('secretary') ||
               lowerSkill.includes('coordinator') || lowerSkill.includes('scheduler') || lowerSkill.includes('receptionist') ||
               lowerSkill.includes('data entry') || lowerSkill.includes('filing') || lowerSkill.includes('record keeping') ||
               lowerSkill.includes('procurement') || lowerSkill.includes('vendor management') || lowerSkill.includes('facilities')) {
        categories.operations.push(skill);
      }
      // Creative & Arts
      else if (lowerSkill.includes('design') || lowerSkill.includes('creative') || lowerSkill.includes('art') ||
               lowerSkill.includes('graphic') || lowerSkill.includes('visual') || lowerSkill.includes('writing') ||
               lowerSkill.includes('content') || lowerSkill.includes('photography') || lowerSkill.includes('video') ||
               lowerSkill.includes('music') || lowerSkill.includes('dance') || lowerSkill.includes('theater') ||
               lowerSkill.includes('fashion') || lowerSkill.includes('interior design') || lowerSkill.includes('architecture')) {
        categories.creative.push(skill);
      }
      // Professional Certifications
      else if (lowerSkill.includes('certification') || lowerSkill.includes('certified') || lowerSkill.includes('license') ||
               lowerSkill.includes('pmp') || lowerSkill.includes('cpa') || lowerSkill.includes('cfa') ||
               lowerSkill.includes('cissp') || lowerSkill.includes('pmp') || lowerSkill.includes('six sigma') ||
               lowerSkill.includes('lean') || lowerSkill.includes('itil') || lowerSkill.includes('scrum') ||
               lowerSkill.includes('agile') || lowerSkill.includes('iso') || lowerSkill.includes('fda')) {
        categories.certifications.push(skill);
      }
      // Other
      else {
        categories.other.push(skill);
      }
    });

    return categories;
  };

  // Process text content to handle HTML and Markdown
  const processTextContent = (text: string) => {
    if (!text) return '';
    
    // First, convert common HTML tags to proper formatting
    let processedText = text
      // Convert <br> and <br/> to line breaks
      .replace(/<br\s*\/?>/gi, '\n')
      // Convert <p> tags to double line breaks
      .replace(/<p[^>]*>/gi, '\n\n')
      .replace(/<\/p>/gi, '')
      // Convert <strong> and <b> to markdown bold
      .replace(/<(strong|b)[^>]*>/gi, '**')
      .replace(/<\/(strong|b)>/gi, '**')
      // Convert <em> and <i> to markdown italic
      .replace(/<(em|i)[^>]*>/gi, '*')
      .replace(/<\/(em|i)>/gi, '*')
      // Convert <h1> to markdown heading
      .replace(/<h1[^>]*>/gi, '# ')
      .replace(/<\/h1>/gi, '\n\n')
      // Convert <h2> to markdown heading
      .replace(/<h2[^>]*>/gi, '## ')
      .replace(/<\/h2>/gi, '\n\n')
      // Convert <h3> to markdown heading
      .replace(/<h3[^>]*>/gi, '### ')
      .replace(/<\/h3>/gi, '\n\n')
      // Convert <ul> and <li> to markdown lists
      .replace(/<ul[^>]*>/gi, '')
      .replace(/<\/ul>/gi, '\n')
      .replace(/<li[^>]*>/gi, '- ')
      .replace(/<\/li>/gi, '\n')
      // Remove any remaining HTML tags
      .replace(/<[^>]*>/g, '')
      // Clean up multiple line breaks
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return processedText;
  };

  // Load existing OCR data when modal opens
  useEffect(() => {
    const loadExistingOCR = async () => {
      if (open && document?.id) {
        try {
          const { data, error } = await supabase
            .from('documents')
            .select('ocr')
            .eq('id', document.id)
            .single();

          if (error) {
            console.error('Error loading existing OCR data:', error);
            setExistingOcr(null);
          } else {
            setExistingOcr(data?.ocr || null);
          }
        } catch (error) {
          console.error('Error loading existing OCR data:', error);
          setExistingOcr(null);
        }
      } else {
        setExistingOcr(null);
      }
    };

    loadExistingOCR();
  }, [open, document?.id]);

  const processImageAnalysis = async () => {
    try {
      console.log('Processing image analysis...');
      setProcessing(true);
      setOcrResult(null);
      
      if (!document?.url) {
        setOcrResult({
          success: false,
          error: 'No document URL available',
          userMessage: 'Document URL is not available for analysis.'
        });
        return;
      }

      console.log('Analyzing image:', document.file_name);
      
      // Call the mistral-image-analysis edge function
      const result = await ApiKeysService.callImageAnalysis(document.url, document.file_name);
      
      if (result.success && result.analysisResult) {
        // Format the analysis results for display
        const analysis = result.analysisResult;
        let extractedText = `Image Analysis Results:\n\n`;
        extractedText += `Description: ${analysis.description || 'No description available'}\n\n`;
        
        if (analysis.objects && analysis.objects.length > 0) {
          extractedText += `Objects detected: ${analysis.objects.join(', ')}\n\n`;
        }
        
        if (analysis.text && analysis.text !== 'No text detected') {
          extractedText += `Text found: ${analysis.text}\n\n`;
        }
        
        if (analysis.faces && analysis.faces > 0) {
          extractedText += `Faces detected: ${analysis.faces}\n\n`;
        }
        
        if (analysis.colors && analysis.colors.length > 0) {
          extractedText += `Colors: ${analysis.colors.join(', ')}\n\n`;
        }
        
        if (analysis.scene && analysis.scene !== 'unknown') {
          extractedText += `Scene: ${analysis.scene}\n\n`;
        }
        
        if (analysis.confidence) {
          extractedText += `Confidence: ${(analysis.confidence * 100).toFixed(1)}%\n\n`;
        }
        
        const statusInfo = `Image Analysis Status:\n\nFile: ${document.file_name}\nType: ${document.file_type}\nSize: ${(document.file_size / 1024).toFixed(1)} KB\n\nAnalysis completed using ${analysis.model || 'Pixtral'} vision models.`;
        
        setOcrResult({
          success: true,
          extractedText: extractedText,
          statusInfo: statusInfo,
          model: analysis.model || 'pixtral-large-latest',
          documentSize: document.file_size,
          usage: { pages_processed: 1 },
          apiKeyId: analysis.apiKeyId
        });
      } else {
        setOcrResult({
          success: false,
          error: result.error || 'Image analysis failed',
          userMessage: result.userMessage || 'Failed to analyze the image. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error in image analysis:', error);
      setOcrResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while analyzing the image.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const processImageTags = async () => {
    try {
      console.log('🏷️ Processing image tags...');
      console.log('📄 Document:', document);
      console.log('🔗 Document URL:', document?.url);
      console.log('🔍 Modal open state:', open);
      console.log('🎯 Auto process flag:', autoProcessImageTags);
      
      setProcessing(true);
      setOcrResult(null);
      
      if (!document?.url) {
        console.error('❌ No document URL available');
        setOcrResult({
          success: false,
          error: 'No document URL available',
          userMessage: 'Document URL is not available for tag generation.'
        });
        setProcessing(false);
        return;
      }

      console.log('🎨 Generating tags for image:', document.file_name);
      console.log('🚀 Calling ApiKeysService.callImageTags...');
      
      // Call the mistral-image-tags edge function
      const result = await ApiKeysService.callImageTags(document.url, document.file_name);
      console.log('📊 API result:', result);
      
      if (result.success && result.tagsResult) {
        // Store the raw tags result for the new beautiful display
        const tagsResult = result.tagsResult;
        
        // Create a special result object for image tags with structured data
        setOcrResult({
          success: true,
          isImageTags: true, // Flag to identify image tags results
          tagsResult: tagsResult,
          extractedText: `🎉 **✨ Image Tags Generated Successfully! ✨** 🎉\n\n🏷️ **All Generated Tags** (${tagsResult.tags?.length || 0} total)\n\n${tagsResult.tags?.join(' • ') || 'No tags generated'}`,
          statusInfo: `**📋 Image Tag Generation Status**\n\n📁 **File:** ${document.file_name}\n📄 **Type:** ${document.file_type}\n💾 **Size:** ${(document.file_size / 1024).toFixed(1)} KB\n\n🤖 **AI Model:** ${tagsResult.model || 'Pixtral'} Vision\n⚡ **Processing:** Complete`,
          model: tagsResult.model || 'pixtral-large-latest',
          documentSize: document.file_size,
          usage: { pages_processed: 1 },
          apiKeyId: tagsResult.apiKeyId
        });
      } else {
        setOcrResult({
          success: false,
          error: result.error || 'Image tag generation failed',
          userMessage: result.userMessage || 'Failed to generate tags for the image. Please try again.'
        });
      }
    } catch (error) {
      console.error('❌ Error in image tag generation:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        modalOpen: open,
        document: document
      });
      setOcrResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while generating image tags.'
      });
    } finally {
      console.log('🏁 processImageTags finally block - setting processing to false');
      setProcessing(false);
    }
  };

  const handleOptionSelect = async (optionId: string) => {
    if (optionId === 'write-ai-app') {
      // If the modal is minimized (widget is visible), restore it
      if (isJobAppWriterMinimized) {
        // First restore the minimized state
        setIsJobAppWriterMinimized(false);
        // Then ensure the modal is open
        setShowAIApplicationWriter(true);
      } else {
        setShowAIApplicationWriter(true);
      }
      return;
    }
    
    if (optionId === 'generate-exercise') {
      // Only show exercise generation for users (not companies)
      if (!companyId) {
        setShowExerciseSelection(true);
      } else {
        console.log('Exercise generation is only available for users, not companies');
      }
      return;
    }
    
    // Check for existing OCR data when selecting OCR option
    if (optionId === 'ocr' && existingOcr) {
      setShowOcrConfirmation(true);
      return;
    }
    
    setSelectedOption(optionId);
    setProcessing(true);
    setOcrResult(null);
    
    try {
      if (optionId === 'ocr' && document?.url) {
        // Process OCR using the new function
        await processOCR();
        return;
      } else if (optionId === 'summarize') {
        // Process text summarization
        await processTextSummarization();
        return;
      } else if (optionId === 'skill-analyze') {
        // Process skill analysis
        await processSkillAnalyze();
        return;
      } else if (optionId === 'test') {
        // Test edge function
        await testNewEdgeFunction();
      } else if (optionId === 'image-analysis') {
        // Process image analysis
        await processImageAnalysis();
        return;
      } else if (optionId === 'image-tags') {
        // Process image tags
        await processImageTags();
        return;
      } else {
        // For other options, simulate processing
        setTimeout(() => {
          setProcessing(false);
          console.log(`Processing document with ${optionId} for document:`, document?.file_name);
        }, 2000);
        return;
      }
    } catch (error) {
      console.error('Error processing document:', error);
      setOcrResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while processing the document.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setSelectedOption(null);
    setProcessing(false);
    setOcrResult(null);
    setSummaryResult(null);
    setSkillAnalyzeResult(null);
    setTestResult(null);
    setShowOcrConfirmation(false);
    onClose();
  };

  const handleCreateNewAnalysis = () => {
    setShowOcrConfirmation(false);
    setSelectedOption('ocr');
    setProcessing(true);
    setOcrResult(null);
    // Continue with OCR processing
    processOCR();
  };

  const handleShowExistingOCR = () => {
    setShowOcrConfirmation(false);
    setSelectedOption('ocr');
    setOcrResult({
      success: true,
      extractedText: existingOcr || undefined,
      userMessage: 'Showing previously extracted text from database.',
      statusInfo: 'This text was previously extracted and saved to the database.'
    });
  };

  const processSummarization = async () => {
    if (!document?.id) return;
    
    try {
      console.log('Starting document summarization...');
      const result = await ApiKeysService.summarizeDocument(
        document.id,
        existingOcr || undefined,
        'detailed',
        500
      );

      console.log('Summarization result:', result);

      if (result.success) {
        setSummaryResult({
          success: true,
          summary: result.summary,
          key_points: result.key_points,
          word_count: result.word_count,
          original_length: result.original_length,
          compression_ratio: result.compression_ratio,
          model: result.model,
          usage: result.usage,
          userMessage: result.userMessage
        });
      } else {
        console.error('Summarization failed:', result);
        setSummaryResult({
          success: false,
          error: result.error,
          userMessage: result.userMessage || result.error || 'Failed to generate summary. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error in summarization:', error);
      setSummaryResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while generating the summary.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const testNewEdgeFunction = async () => {
    try {
      console.log('Testing new edge function...');
      setProcessing(true);
      setTestResult(null);
      
      const result = await ApiKeysService.testNewEdgeFunction();
      console.log('Test result:', result);
      
      setTestResult(result);
    } catch (error) {
      console.error('Error testing new edge function:', error);
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while testing the new edge function.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const testConnection = async () => {
    try {
      console.log('Testing connection to edge function...');
      setProcessing(true);
      setTestResult(null);
      
      const result = await ApiKeysService.testConnection();
      console.log('Connection test result:', result);
      
      setTestResult(result);
    } catch (error) {
      console.error('Error testing connection:', error);
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while testing connection.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const processTextSummarization = async () => {
    try {
      console.log('Processing text summarization...');
      setProcessing(true);
      setSummaryResult(null);
      
      // Check if we have existing OCR data
      if (!existingOcr || existingOcr.trim().length === 0) {
        setSummaryResult({
          success: false,
          error: 'No text data available',
          userMessage: 'Please extract text from the document first using Text Extraction, or ensure the document has OCR data available.'
        });
        return;
      }

      console.log('Summarizing text with length:', existingOcr.length);
      
      // Choose the correct service method based on context
      const result = companyId 
        ? await ApiKeysService.summarizeText(
            existingOcr,
            'detailed',
            500,
            document?.id,
            companyId
          )
        : await ApiKeysService.summarizeTextUser(
            existingOcr,
            'detailed',
            500,
            document?.id
          );
      
      console.log('Text summarization result:', result);
      
      if (result.success) {
        setSummaryResult({
          success: true,
          summary: result.summary,
          key_points: result.keyPoints,
          word_count: result.wordCount,
          original_length: result.originalLength,
          compression_ratio: result.compressionRatio,
          model: result.model,
          usage: result.usage,
          userMessage: result.userMessage,
          context: result.context
        });
      } else {
        console.error('Text summarization failed:', result);
        setSummaryResult({
          success: false,
          error: result.error,
          userMessage: result.userMessage || result.error || 'Failed to generate summary. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error in text summarization:', error);
      setSummaryResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while generating the summary.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const processSkillAnalyze = async () => {
    try {
      console.log('Processing skill analysis...');
      setProcessing(true);
      setSkillAnalyzeResult(null);
      
      // Check if we have existing OCR data
      if (!existingOcr || existingOcr.trim().length === 0) {
        setSkillAnalyzeResult({
          success: false,
          error: 'No text data available',
          userMessage: 'Please extract text from the document first using Text Extraction, or ensure the document has OCR data available.'
        });
        return;
      }

      console.log('Analyzing skills from text with length:', existingOcr.length);
      
      // Use the mistral-skills edge function for skill analysis
      const result = await ApiKeysService.analyzeSkills(
        existingOcr,
        'detailed',
        500,
        document?.id,
        companyId || undefined
      );
      
      console.log('Skill analysis result:', result);
      
      if (result.success) {
        setSkillAnalyzeResult({
          success: true,
          skills: result.skills,
          skill_count: result.skillCount,
          original_length: result.originalLength,
          model: result.model,
          usage: result.usage,
          userMessage: result.userMessage,
          context: result.context
        });
      } else {
        console.error('Skill analysis failed:', result);
        setSkillAnalyzeResult({
          success: false,
          error: result.error,
          userMessage: result.userMessage || result.error || 'Failed to analyze skills. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error in skill analysis:', error);
      setSkillAnalyzeResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while analyzing skills.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const processOCR = async () => {
    if (!document?.url) return;
    
    try {
      // Call mistral-ocr-latest edge function
      console.log('Calling mistral-ocr-latest edge function...');
      const mistralResult = await ApiKeysService.callMistralOCR(document.url, document.file_name);
      console.log('Mistral OCR result:', mistralResult);
      
      // Create user message with bucket access info
      let userMessage = '';
      if (mistralResult.success) {
        const bucketStatus = mistralResult.bucketAccess ? '✅' : '❌';
        userMessage = `Mistral OCR function ready! ${bucketStatus} ${mistralResult.bucketMessage || 'Bucket access unknown'}`;
      } else {
        userMessage = (mistralResult as any).userMessage || 'Mistral OCR call failed';
      }

      // Handle OCR results - separate status from content
      let extractedText = '';
      let statusInfo = '';
      let ocrSuccess = false;
      
      // Build comprehensive status message
      if (mistralResult.success) {
        statusInfo = `${mistralResult.message}\n\nBucket Access: ${mistralResult.bucketAccess ? '✅' : '❌'} ${mistralResult.bucketMessage || 'Unknown'}\n\nDocument Check: ${mistralResult.documentFound ? '✅' : '❌'} ${mistralResult.documentMessage || 'Unknown'}${mistralResult.documentSizeFormatted ? `\n\nFile Size: ${mistralResult.documentSizeFormatted}` : ''}`;
      }
      
      if (mistralResult.success && mistralResult.ocrResult) {
        if (mistralResult.ocrResult.success) {
          ocrSuccess = true;
          extractedText = mistralResult.ocrResult.extractedText || 'No text extracted';
        } else {
          const errorDetails = (mistralResult.ocrResult as any).details ? 
            `\n\nDetails: ${(mistralResult.ocrResult as any).details}` : '';
          extractedText = `OCR Error: ${mistralResult.ocrResult.error || 'Unknown error'}${errorDetails}`;
        }
      } else {
        extractedText = 'Processing failed';
      }

      setOcrResult({
        success: ocrSuccess || mistralResult.success,
        extractedText: extractedText,
        statusInfo: statusInfo,
        error: mistralResult.error || (mistralResult.ocrResult?.error),
        userMessage: ocrSuccess ? 
          `OCR completed successfully! Extracted ${mistralResult.ocrResult?.extractedText?.length || 0} characters using ${mistralResult.ocrResult?.model || 'Mistral OCR'}.` :
          userMessage,
        model: mistralResult.ocrResult?.model,
        usage: mistralResult.ocrResult?.usage,
        details: mistralResult.bucketMessage ? `Bucket access: ${mistralResult.bucketMessage}` : undefined
      });

      // Save OCR result to database if successful
      if (ocrSuccess && document?.id && extractedText) {
        try {
          console.log('Saving OCR result to database for document:', document.id);
          const { error: updateError } = await supabase
            .from('documents')
            .update({ 
              ocr: extractedText,
              updated_at: new Date().toISOString()
            })
            .eq('id', document.id);

          if (updateError) {
            console.error('Error saving OCR result to database:', updateError);
            // Update the result to show the save error
            setOcrResult(prev => prev ? {
              ...prev,
              userMessage: (prev.userMessage || '') + ' (Note: OCR result could not be saved to database)',
              error: updateError.message
            } : null);
          } else {
            console.log('OCR result saved to database successfully');
            // Update the existing OCR state with the new content
            setExistingOcr(extractedText);
            // Update the result to show successful save
            setOcrResult(prev => prev ? {
              ...prev,
              userMessage: (prev.userMessage || '') + ' (OCR result saved to database)'
            } : null);
          }
        } catch (dbError) {
          console.error('Database error while saving OCR result:', dbError);
          setOcrResult(prev => prev ? {
            ...prev,
            userMessage: (prev.userMessage || '') + ' (Note: OCR result could not be saved to database)',
            error: dbError instanceof Error ? dbError.message : 'Database save failed'
          } : null);
        }
      }
    } catch (error) {
      console.error('Error processing document:', error);
      setOcrResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while processing the document.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <DocumentIcon />;
    if (fileType.includes('image')) return <ImageIcon />;
    return <DocumentIcon />;
  };

  const groupedOptions = getAIOptions(!!companyId, document || undefined).reduce((acc: Record<string, AIOption[]>, option: AIOption) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push(option);
    return acc;
  }, {} as Record<string, AIOption[]>);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isMaximized ? false : "md"}
      fullWidth={isMaximized}
      fullScreen={isMaximized}
      PaperProps={{
        sx: {
          borderRadius: isMaximized ? 0 : 3,
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          minHeight: isMaximized ? '100vh' : '600px',
          maxHeight: isMaximized ? '100vh' : '90vh',
        },
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AIIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                AI Document Processing
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Choose an AI-powered tool to analyze your document
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton 
              onClick={() => setIsMaximized(!isMaximized)} 
              sx={{ color: '#64748b' }}
              title={isMaximized ? 'Restore' : 'Maximize'}
            >
              <MaximizeIcon />
            </IconButton>
            <IconButton onClick={handleClose} sx={{ color: '#64748b' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 1 }}>
        {/* Document Info */}
        {document && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {getFileIcon(document.file_type)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {document.file_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={document.document_type}
                    size="small"
                    sx={{
                      bgcolor: alpha('#3b82f6', 0.1),
                      color: '#3b82f6',
                      fontWeight: 500,
                    }}
                  />
                  <Chip
                    label={`${(document.file_size / 1024).toFixed(1)} KB`}
                    size="small"
                    variant="outlined"
                    sx={{ color: '#64748b' }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* OCR Result Display - Moved to Top */}
        <AnimatePresence>
          {ocrResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  mb: 3,
                  borderRadius: 3,
                  background: ocrResult.success
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%)',
                  border: ocrResult.success
                    ? '1px solid rgba(16, 185, 129, 0.2)'
                    : '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  {ocrResult.success ? (
                    <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
                  ) : (
                    <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                  )}
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    {ocrResult.success ? 'OCR Processing Successful!' : 'OCR Processing Failed!!!!'}
                  </Typography>
                </Box>
                
                {ocrResult.success ? (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      Successfully extracted text from your document using Mistral AI OCR.
                    </Typography>
                    
                    {/* Status Information Container */}
                    {ocrResult.statusInfo && (
                      <Paper
                        elevation={1}
                        sx={{
                          p: 3,
                          mb: 3,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${alpha('#10b981', 0.08)} 0%, ${alpha('#3b82f6', 0.08)} 100%)`,
                          border: `2px solid ${alpha('#10b981', 0.3)}`,
                          position: 'relative',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            background: `linear-gradient(135deg, ${alpha('#10b981', 0.9)} 0%, ${alpha('#3b82f6', 0.9)} 100%)`,
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            borderRadius: '0 12px 0 12px',
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                        >
                          📊 Status
                        </Box>
                        
                        <Box sx={{ pr: 6, pt: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.6,
                              color: 'text.primary',
                              whiteSpace: 'pre-line',
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                            }}
                          >
                            {ocrResult.statusInfo}
                          </Typography>
                        </Box>
                      </Paper>
                    )}
                    
                    {/* Display existing OCR content if available */}
                    {existingOcr && !ocrResult?.extractedText && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 3, color: '#059669', fontSize: '1.1rem' }}>
                          📄 Previously Extracted Text:
                        </Typography>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${alpha('#10b981', 0.05)} 0%, ${alpha('#3b82f6', 0.05)} 100%)`,
                            border: `2px solid ${alpha('#10b981', 0.2)}`,
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              background: `linear-gradient(135deg, ${alpha('#10b981', 0.9)} 0%, ${alpha('#3b82f6', 0.9)} 100%)`,
                              color: 'white',
                              px: 2,
                              py: 0.5,
                              borderRadius: '0 12px 0 12px',
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                          >
                            SAVED
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.6,
                              color: '#374151',
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {processTextContent(existingOcr)}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {ocrResult.extractedText && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 3, color: '#059669', fontSize: '1.1rem' }}>
                          📄 Extracted Text:
                        </Typography>
                        
                        {/* Split text by page breaks and display each page */}
                        {ocrResult.extractedText.split('\n\n--- Page Break ---\n\n').map((pageContent, index) => (
                          <Paper
                            key={`page-${index}`}
                            elevation={2}
                            sx={{
                              p: 3,
                              mb: 3,
                              borderRadius: 3,
                              background: `linear-gradient(135deg, ${alpha('#10b981', 0.05)} 0%, ${alpha('#3b82f6', 0.05)} 100%)`,
                              border: `2px solid ${alpha('#10b981', 0.2)}`,
                              position: 'relative',
                              overflow: 'hidden',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.15)',
                                transition: 'all 0.3s ease-in-out',
                              },
                            }}
                          >
                            {/* Page Header */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                background: `linear-gradient(135deg, ${alpha('#10b981', 0.9)} 0%, ${alpha('#3b82f6', 0.9)} 100%)`,
                                color: 'white',
                                px: 2,
                                py: 0.5,
                                borderRadius: '0 12px 0 12px',
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              }}
                            >
                              📄 Page {index + 1}
                            </Box>
                            
                            {/* Page Content */}
                            <Box sx={{ pr: 6, pt: 1 }}>
                              <ReactMarkdown
                                components={{
                                  h1: ({ children }) => (
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, mt: 2 }}>
                                      {children}
                                    </Typography>
                                  ),
                                  h2: ({ children }) => (
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, mt: 2 }}>
                                      {children}
                                    </Typography>
                                  ),
                                  h3: ({ children }) => (
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, mt: 1 }}>
                                      {children}
                                    </Typography>
                                  ),
                                  h4: ({ children }) => (
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, mt: 1 }}>
                                      {children}
                                    </Typography>
                                  ),
                                  h5: ({ children }) => (
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, mt: 1 }}>
                                      {children}
                                    </Typography>
                                  ),
                                  h6: ({ children }) => (
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, mt: 1 }}>
                                      {children}
                                    </Typography>
                                  ),
                                  strong: ({ children }) => (
                                    <Typography component="span" sx={{ fontWeight: 'bold', color: '#d97706' }}>
                                      {children}
                                    </Typography>
                                  ),
                                  em: ({ children }) => (
                                    <Typography component="span" sx={{ fontStyle: 'italic', color: '#059669' }}>
                                      {children}
                                    </Typography>
                                  ),
                                  p: ({ children }) => {
                                    const content = children?.toString() || '';
                                    
                                    // Check if this paragraph contains tags (bullet-separated content)
                                    const isTagContent = content.includes('•') && 
                                      content.length > 0 && 
                                      content.length < 200 && // Reasonable length for tags
                                      !content.includes('\n') && 
                                      !content.includes('```');
                                    
                                    if (isTagContent) {
                                      // Split tags by • and clean them up
                                      const tags = content.split('•').map(tag => tag.trim()).filter(tag => tag && tag.length > 0);
                                      
                                      return (
                                        <Box sx={{ 
                                          display: 'flex', 
                                          flexWrap: 'wrap', 
                                          gap: 1, 
                                          my: 1,
                                          alignItems: 'center'
                                        }}>
                                          {tags.map((tag, index) => (
                                            <motion.div
                                              key={index}
                                              whileHover={{ 
                                                scale: 1.05,
                                                y: -2,
                                                transition: { duration: 0.2 }
                                              }}
                                              whileTap={{ scale: 0.95 }}
                                            >
                                              <Box
                                                sx={{
                                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                  color: 'white',
                                                  padding: '6px 12px',
                                                  borderRadius: '20px',
                                                  fontSize: '0.8rem',
                                                  fontWeight: '500',
                                                  display: 'inline-block',
                                                  cursor: 'pointer',
                                                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                                  transition: 'all 0.3s ease',
                                                  whiteSpace: 'nowrap',
                                                  '&:hover': {
                                                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                                    transform: 'translateY(-1px)',
                                                  }
                                                }}
                                              >
                                                {tag}
                                              </Box>
                                            </motion.div>
                                          ))}
                                        </Box>
                                      );
                                    }
                                    
                                    return (
                                      <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
                                        {children}
                                      </Typography>
                                    );
                                  },
                                  ul: ({ children }) => (
                                    <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                                      {children}
                                    </Box>
                                  ),
                                  ol: ({ children }) => (
                                    <Box component="ol" sx={{ pl: 2, mb: 1 }}>
                                      {children}
                                    </Box>
                                  ),
                                  li: ({ children }) => (
                                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                      {children}
                                    </Typography>
                                  ),
                                  a: ({ href, children }) => (
                                    <Typography
                                      component="a"
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        color: '#0891b2',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          color: '#0e7490',
                                        },
                                      }}
                                    >
                                      {children}
                                    </Typography>
                                  ),
                                  table: ({ children }) => (
                                    <TableContainer 
                                      component={Paper} 
                                      sx={{ 
                                        mt: 2, 
                                        mb: 2, 
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        overflow: 'hidden'
                                      }}
                                    >
                                      <Table size="small" aria-label="extracted table">
                                        {children}
                                      </Table>
                                    </TableContainer>
                                  ),
                                  thead: ({ children }) => (
                                    <TableHead sx={{ bgcolor: alpha('#3b82f6', 0.1) }}>
                                      {children}
                                    </TableHead>
                                  ),
                                  tbody: ({ children }) => <TableBody>{children}</TableBody>,
                                  tr: ({ children }) => <TableRow>{children}</TableRow>,
                                  th: ({ children }) => (
                                    <TableCell 
                                      sx={{ 
                                        fontWeight: 'bold', 
                                        border: '1px solid #e0e0e0', 
                                        p: 1,
                                        bgcolor: alpha('#3b82f6', 0.05)
                                      }}
                                    >
                                      {children}
                                    </TableCell>
                                  ),
                                  td: ({ children }) => (
                                    <TableCell sx={{ border: '1px solid #e0e0e0', p: 1 }}>
                                      {children}
                                    </TableCell>
                                  ),
                                  code: ({ children }) => {
                                    const content = children?.toString() || '';
                                    
                                    // Better detection: check if content contains • (bullet separator) or looks like tags
                                    const isTagContent = content.includes('•') || 
                                      (content.length > 0 && content.length < 50 && !content.includes('\n') && !content.includes('```'));
                                    
                                    if (isTagContent) {
                                      // Split tags by • and clean them up, removing any backticks that might remain
                                      const tags = content.split('•').map(tag => tag.trim().replace(/`/g, '')).filter(tag => tag && tag.length > 0);
                                      
                                      return (
                                        <Box sx={{ 
                                          display: 'flex', 
                                          flexWrap: 'wrap', 
                                          gap: 1, 
                                          my: 1,
                                          alignItems: 'center'
                                        }}>
                                          {tags.map((tag, index) => (
                                            <motion.div
                                              key={index}
                                              whileHover={{ 
                                                scale: 1.05,
                                                y: -2,
                                                transition: { duration: 0.2 }
                                              }}
                                              whileTap={{ scale: 0.95 }}
                                            >
                                              <Box
                                                sx={{
                                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                  color: 'white',
                                                  padding: '6px 12px',
                                                  borderRadius: '20px',
                                                  fontSize: '0.8rem',
                                                  fontWeight: '500',
                                                  display: 'inline-block',
                                                  cursor: 'pointer',
                                                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                                  transition: 'all 0.3s ease',
                                                  whiteSpace: 'nowrap',
                                                  '&:hover': {
                                                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                                    transform: 'translateY(-1px)',
                                                  }
                                                }}
                                              >
                                                {tag}
                                              </Box>
                                            </motion.div>
                                          ))}
                                        </Box>
                                      );
                                    }
                                    
                                    // Default code styling for non-tag code
                                    return (
                                      <Typography
                                        component="code"
                                        sx={{
                                          backgroundColor: alpha('#f3f4f6', 0.8),
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          fontFamily: 'monospace',
                                          fontSize: '0.875rem',
                                          border: '1px solid #e5e7eb',
                                        }}
                                      >
                                        {children}
                                      </Typography>
                                    );
                                  },
                                  blockquote: ({ children }) => (
                                    <Box
                                      sx={{
                                        borderLeft: '4px solid #10b981',
                                        pl: 2,
                                        py: 1,
                                        mb: 1,
                                        backgroundColor: alpha('#10b981', 0.05),
                                        fontStyle: 'italic',
                                        borderRadius: '0 8px 8px 0',
                                      }}
                                    >
                                      {children}
                                    </Box>
                                  ),
                                }}
                              >
                                {processTextContent(pageContent.trim())}
                              </ReactMarkdown>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}
                    
                    {/* Formatting Information Section */}
                    {ocrResult.formattingInfo && (
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          mt: 2,
                          bgcolor: alpha('#3b82f6', 0.05),
                          border: `1px solid ${alpha('#3b82f6', 0.2)}`,
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1e40af' }}>
                          📋 Document Structure Detected:
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Headings */}
                          {ocrResult.formattingInfo.headings.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                                📑 Headings ({ocrResult.formattingInfo.headings.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {ocrResult.formattingInfo.headings.map((heading, index) => (
                                  <Chip
                                    key={index}
                                    label={`H${heading.level}: ${heading.text}`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#3b82f6', 0.1), 
                                      color: '#1e40af',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          
                          {/* Bold Text */}
                          {ocrResult.formattingInfo.boldText.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                                <strong>Bold Text</strong> ({ocrResult.formattingInfo.boldText.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {ocrResult.formattingInfo.boldText.slice(0, 10).map((bold, index) => (
                                  <Chip
                                    key={index}
                                    label={bold.text}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#f59e0b', 0.1), 
                                      color: '#d97706',
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                ))}
                                {ocrResult.formattingInfo.boldText.length > 10 && (
                                  <Chip
                                    label={`+${ocrResult.formattingInfo.boldText.length - 10} more`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#6b7280', 0.1), 
                                      color: '#6b7280',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          )}
                          
                          {/* Italic Text */}
                          {ocrResult.formattingInfo.italicText.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                                <em>Italic Text</em> ({ocrResult.formattingInfo.italicText.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {ocrResult.formattingInfo.italicText.slice(0, 10).map((italic, index) => (
                                  <Chip
                                    key={index}
                                    label={italic.text}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#10b981', 0.1), 
                                      color: '#059669',
                                      fontSize: '0.75rem',
                                      fontStyle: 'italic'
                                    }}
                                  />
                                ))}
                                {ocrResult.formattingInfo.italicText.length > 10 && (
                                  <Chip
                                    label={`+${ocrResult.formattingInfo.italicText.length - 10} more`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#6b7280', 0.1), 
                                      color: '#6b7280',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          )}
                          
                          {/* Lists */}
                          {ocrResult.formattingInfo.lists.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                                📝 Lists ({ocrResult.formattingInfo.lists.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {ocrResult.formattingInfo.lists.map((list, index) => (
                                  <Chip
                                    key={index}
                                    label={`${list.type === 'ordered' ? '1.' : '•'} ${list.items.length} items`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#8b5cf6', 0.1), 
                                      color: '#7c3aed',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          
                          {/* Tables */}
                          {ocrResult.formattingInfo.tables.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                                📊 Tables ({ocrResult.formattingInfo.tables.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {ocrResult.formattingInfo.tables.map((table, index) => (
                                  <Chip
                                    key={index}
                                    label={`${table.rows.length} rows × ${table.rows[0]?.length || 0} cols`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#ef4444', 0.1), 
                                      color: '#dc2626',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          
                          {/* Links */}
                          {ocrResult.formattingInfo.links.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                                🔗 Links ({ocrResult.formattingInfo.links.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {ocrResult.formattingInfo.links.slice(0, 5).map((link, index) => (
                                  <Chip
                                    key={index}
                                    label={link.text}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#06b6d4', 0.1), 
                                      color: '#0891b2',
                                      fontSize: '0.75rem'
                                    }}
                                    onClick={() => window.open(link.url, '_blank')}
                                    style={{ cursor: 'pointer' }}
                                  />
                                ))}
                                {ocrResult.formattingInfo.links.length > 5 && (
                                  <Chip
                                    label={`+${ocrResult.formattingInfo.links.length - 5} more`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#6b7280', 0.1), 
                                      color: '#6b7280',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    )}
                    
                    {ocrResult.model && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip
                          label={`Model: ${ocrResult.model}`}
                          size="small"
                          sx={{ bgcolor: alpha('#10b981', 0.1), color: '#059669' }}
                        />
                        {ocrResult.documentSize && (
                          <Chip
                            label={`Size: ${(ocrResult.documentSize / 1024).toFixed(1)} KB`}
                            size="small"
                            sx={{ bgcolor: alpha('#10b981', 0.1), color: '#059669' }}
                          />
                        )}
                        {ocrResult.usage && (
                          <Chip
                            label={`Pages: ${ocrResult.usage.pages_processed || ocrResult.usage.total_tokens || 'N/A'}`}
                            size="small"
                            sx={{ bgcolor: alpha('#10b981', 0.1), color: '#059669' }}
                          />
                        )}
                        {ocrResult.apiKeyId && (
                          <Chip
                            label={`API Key: ${ocrResult.apiKeyId.substring(0, 8)}...`}
                            size="small"
                            sx={{ bgcolor: alpha('#10b981', 0.1), color: '#059669' }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Beautiful Image Tags Display */}
                    {ocrResult.isImageTags && ocrResult.tagsResult && (
                      <Box sx={{ mt: 3 }}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: 0.2 }}
                        >
                          {/* Header with Success Message */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            mb: 4,
                            p: 3,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                            border: '2px solid rgba(16, 185, 129, 0.3)',
                          }}>
                            <Box sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                            }}>
                              <AnalyticsIcon sx={{ color: 'white', fontSize: 24 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h5" sx={{ 
                                fontWeight: 700, 
                                color: '#1e293b',
                                mb: 0.5,
                                background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                              }}>
                                🎉 Image Tags Generated Successfully!
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#64748b' }}>
                                AI-powered image analysis completed with {ocrResult.tagsResult.tags?.length || 0} tags across multiple categories
                              </Typography>
                            </Box>
                          </Box>

                          {/* Summary Statistics */}
                          <Paper elevation={2} sx={{ 
                            p: 3, 
                            mb: 3, 
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                          }}>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 600, 
                              mb: 2, 
                              color: '#1e293b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}>
                              <TrendingUpIcon sx={{ color: '#10b981' }} />
                              Analysis Summary
                            </Typography>
                            <Box sx={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                              gap: 2 
                            }}>
                              <Box sx={{ 
                                p: 2, 
                                borderRadius: 2, 
                                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                                border: '1px solid rgba(16, 185, 129, 0.2)',
                              }}>
                                <Typography variant="h4" sx={{ 
                                  fontWeight: 700, 
                                  color: '#10b981',
                                  mb: 0.5,
                                }}>
                                  {ocrResult.tagsResult.tags?.length || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                  Total Tags Generated
                                </Typography>
                              </Box>
                              <Box sx={{ 
                                p: 2, 
                                borderRadius: 2, 
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                                border: '1px solid rgba(59, 130, 246, 0.2)',
                              }}>
                                <Typography variant="h4" sx={{ 
                                  fontWeight: 700, 
                                  color: '#3b82f6',
                                  mb: 0.5,
                                }}>
                                  {Object.keys(ocrResult.tagsResult.categories || {}).length}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                  Categories Detected
                                </Typography>
                              </Box>
                              <Box sx={{ 
                                p: 2, 
                                borderRadius: 2, 
                                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                                border: '1px solid rgba(245, 158, 11, 0.2)',
                              }}>
                                <Typography variant="h4" sx={{ 
                                  fontWeight: 700, 
                                  color: '#f59e0b',
                                  mb: 0.5,
                                }}>
                                  {ocrResult.tagsResult.confidence ? `${(ocrResult.tagsResult.confidence * 100).toFixed(0)}%` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                  Confidence Score
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>

                          {/* Category Cards */}
                          {ocrResult.tagsResult.categories && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 600, 
                                mb: 3, 
                                color: '#1e293b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}>
                                <CategoryIcon sx={{ color: '#8b5cf6' }} />
                                Tag Categories
                              </Typography>
                              <Box sx={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                                gap: 3 
                              }}>
                                {/* People & Faces */}
                                {ocrResult.tagsResult.categories.people && ocrResult.tagsResult.categories.people.length > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                  >
                                    <Paper elevation={3} sx={{ 
                                      p: 3, 
                                      borderRadius: 3,
                                      background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(236, 72, 153, 0.05) 100%)',
                                      border: '2px solid rgba(236, 72, 153, 0.3)',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 24px rgba(236, 72, 153, 0.2)',
                                        transition: 'all 0.3s ease-in-out',
                                      },
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: '50%',
                                          background: 'linear-gradient(135deg, #ec4899 0%, #f97316 100%)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}>
                                          <PeopleIcon sx={{ color: 'white', fontSize: 20 }} />
                                        </Box>
                                        <Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            People & Faces
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            {ocrResult.tagsResult.categories.people.length} tags
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {ocrResult.tagsResult.categories.people.map((tag: string, index: number) => (
                                          <motion.div
                                            key={tag}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                                          >
                                            <Chip
                                              label={tag}
                                              sx={{
                                                background: 'linear-gradient(135deg, #ec4899 0%, #f97316 100%)',
                                                color: 'white',
                                                fontWeight: 500,
                                                '&:hover': {
                                                  transform: 'scale(1.05)',
                                                  boxShadow: '0 4px 12px rgba(236, 72, 153, 0.4)',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                              }}
                                            />
                                          </motion.div>
                                        ))}
                                      </Box>
                                    </Paper>
                                  </motion.div>
                                )}

                                {/* Objects & Items */}
                                {ocrResult.tagsResult.categories.objects && ocrResult.tagsResult.categories.objects.length > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: 0.4 }}
                                  >
                                    <Paper elevation={3} sx={{ 
                                      p: 3, 
                                      borderRadius: 3,
                                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                                      border: '2px solid rgba(16, 185, 129, 0.3)',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 24px rgba(16, 185, 129, 0.2)',
                                        transition: 'all 0.3s ease-in-out',
                                      },
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: '50%',
                                          background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}>
                                          <CategoryIcon sx={{ color: 'white', fontSize: 20 }} />
                                        </Box>
                                        <Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            Objects & Items
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            {ocrResult.tagsResult.categories.objects.length} tags
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {ocrResult.tagsResult.categories.objects.map((tag: string, index: number) => (
                                          <motion.div
                                            key={tag}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                                          >
                                            <Chip
                                              label={tag}
                                              sx={{
                                                background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                                                color: 'white',
                                                fontWeight: 500,
                                                '&:hover': {
                                                  transform: 'scale(1.05)',
                                                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                              }}
                                            />
                                          </motion.div>
                                        ))}
                                      </Box>
                                    </Paper>
                                  </motion.div>
                                )}

                                {/* Colors & Visual */}
                                {ocrResult.tagsResult.categories.colors && ocrResult.tagsResult.categories.colors.length > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: 0.5 }}
                                  >
                                    <Paper elevation={3} sx={{ 
                                      p: 3, 
                                      borderRadius: 3,
                                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                                      border: '2px solid rgba(245, 158, 11, 0.3)',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 24px rgba(245, 158, 11, 0.2)',
                                        transition: 'all 0.3s ease-in-out',
                                      },
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: '50%',
                                          background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}>
                                          <PaletteIcon sx={{ color: 'white', fontSize: 20 }} />
                                        </Box>
                                        <Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            Colors & Visual
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            {ocrResult.tagsResult.categories.colors.length} tags
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {ocrResult.tagsResult.categories.colors.map((tag: string, index: number) => (
                                          <motion.div
                                            key={tag}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                                          >
                                            <Chip
                                              label={tag}
                                              sx={{
                                                background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                                                color: 'white',
                                                fontWeight: 500,
                                                '&:hover': {
                                                  transform: 'scale(1.05)',
                                                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                              }}
                                            />
                                          </motion.div>
                                        ))}
                                      </Box>
                                    </Paper>
                                  </motion.div>
                                )}

                                {/* Locations & Scenes */}
                                {ocrResult.tagsResult.categories.locations && ocrResult.tagsResult.categories.locations.length > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: 0.6 }}
                                  >
                                    <Paper elevation={3} sx={{ 
                                      p: 3, 
                                      borderRadius: 3,
                                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                                      border: '2px solid rgba(59, 130, 246, 0.3)',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 24px rgba(59, 130, 246, 0.2)',
                                        transition: 'all 0.3s ease-in-out',
                                      },
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: '50%',
                                          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}>
                                          <LocationIcon sx={{ color: 'white', fontSize: 20 }} />
                                        </Box>
                                        <Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            Locations & Scenes
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            {ocrResult.tagsResult.categories.locations.length} tags
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {ocrResult.tagsResult.categories.locations.map((tag: string, index: number) => (
                                          <motion.div
                                            key={tag}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: 0.7 + index * 0.1 }}
                                          >
                                            <Chip
                                              label={tag}
                                              sx={{
                                                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                                                color: 'white',
                                                fontWeight: 500,
                                                '&:hover': {
                                                  transform: 'scale(1.05)',
                                                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                              }}
                                            />
                                          </motion.div>
                                        ))}
                                      </Box>
                                    </Paper>
                                  </motion.div>
                                )}

                                {/* Activities & Actions */}
                                {ocrResult.tagsResult.categories.activities && ocrResult.tagsResult.categories.activities.length > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: 0.7 }}
                                  >
                                    <Paper elevation={3} sx={{ 
                                      p: 3, 
                                      borderRadius: 3,
                                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
                                      border: '2px solid rgba(139, 92, 246, 0.3)',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 24px rgba(139, 92, 246, 0.2)',
                                        transition: 'all 0.3s ease-in-out',
                                      },
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: '50%',
                                          background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}>
                                          <SportsIcon sx={{ color: 'white', fontSize: 20 }} />
                                        </Box>
                                        <Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            Activities & Actions
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            {ocrResult.tagsResult.categories.activities.length} tags
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {ocrResult.tagsResult.categories.activities.map((tag: string, index: number) => (
                                          <motion.div
                                            key={tag}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: 0.8 + index * 0.1 }}
                                          >
                                            <Chip
                                              label={tag}
                                              sx={{
                                                background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                                                color: 'white',
                                                fontWeight: 500,
                                                '&:hover': {
                                                  transform: 'scale(1.05)',
                                                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                              }}
                                            />
                                          </motion.div>
                                        ))}
                                      </Box>
                                    </Paper>
                                  </motion.div>
                                )}

                                {/* Emotions & Mood */}
                                {ocrResult.tagsResult.categories.emotions && ocrResult.tagsResult.categories.emotions.length > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: 0.8 }}
                                  >
                                    <Paper elevation={3} sx={{ 
                                      p: 3, 
                                      borderRadius: 3,
                                      background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(236, 72, 153, 0.05) 100%)',
                                      border: '2px solid rgba(236, 72, 153, 0.3)',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 24px rgba(236, 72, 153, 0.2)',
                                        transition: 'all 0.3s ease-in-out',
                                      },
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: '50%',
                                          background: 'linear-gradient(135deg, #ec4899 0%, #f59e0b 100%)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}>
                                          <EmojiIcon sx={{ color: 'white', fontSize: 20 }} />
                                        </Box>
                                        <Box>
                                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                            Emotions & Mood
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            {ocrResult.tagsResult.categories.emotions.length} tags
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {ocrResult.tagsResult.categories.emotions.map((tag: string, index: number) => (
                                          <motion.div
                                            key={tag}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: 0.9 + index * 0.1 }}
                                          >
                                            <Chip
                                              label={tag}
                                              sx={{
                                                background: 'linear-gradient(135deg, #ec4899 0%, #f59e0b 100%)',
                                                color: 'white',
                                                fontWeight: 500,
                                                '&:hover': {
                                                  transform: 'scale(1.05)',
                                                  boxShadow: '0 4px 12px rgba(236, 72, 153, 0.4)',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                              }}
                                            />
                                          </motion.div>
                                        ))}
                                      </Box>
                                    </Paper>
                                  </motion.div>
                                )}
                              </Box>
                            </Box>
                          )}

                          {/* All Tags Summary */}
                          {ocrResult.tagsResult.tags && ocrResult.tagsResult.tags.length > 0 && (
                            <Paper elevation={2} sx={{ 
                              p: 3, 
                              borderRadius: 3,
                              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                              border: '1px solid rgba(226, 232, 240, 0.8)',
                            }}>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 600, 
                                mb: 2, 
                                color: '#1e293b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}>
                                <StarIcon sx={{ color: '#f59e0b' }} />
                                All Generated Tags
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {ocrResult.tagsResult.tags.map((tag: string, index: number) => (
                                  <motion.div
                                    key={tag}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.3, delay: 1.0 + index * 0.05 }}
                                  >
                                    <Chip
                                      label={tag}
                                      sx={{
                                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                        color: 'white',
                                        fontWeight: 500,
                                        '&:hover': {
                                          transform: 'scale(1.05)',
                                          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                      }}
                                    />
                                  </motion.div>
                                ))}
                              </Box>
                            </Paper>
                          )}

                          {/* Action Buttons */}
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            mt: 3,
                            flexWrap: 'wrap',
                          }}>
                            <Button
                              variant="contained"
                              startIcon={<CopyIcon />}
                              onClick={() => {
                                const allTags = ocrResult.tagsResult?.tags?.join(', ') || '';
                                navigator.clipboard.writeText(allTags);
                                toast.success('Tags copied to clipboard!');
                              }}
                              sx={{
                                background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #059669 0%, #0891b2 100%)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 8px 16px rgba(16, 185, 129, 0.3)',
                                },
                                transition: 'all 0.3s ease-in-out',
                              }}
                            >
                              Copy All Tags
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => {
                                const tagsData = {
                                  tags: ocrResult.tagsResult?.tags || [],
                                  categories: ocrResult.tagsResult?.categories || {},
                                  confidence: ocrResult.tagsResult?.confidence || 0,
                                  model: ocrResult.tagsResult?.model || 'pixtral-large-latest',
                                  timestamp: new Date().toISOString(),
                                };
                                const blob = new Blob([JSON.stringify(tagsData, null, 2)], { type: 'application/json' });
                                const url = URL.createObjectURL(blob);
                                const a = window.document.createElement('a');
                                a.href = url;
                                a.download = `image-tags-${document?.file_name?.split('.')[0] || 'image'}.json`;
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success('Tags exported successfully!');
                              }}
                              sx={{
                                borderColor: '#3b82f6',
                                color: '#3b82f6',
                                '&:hover': {
                                  borderColor: '#2563eb',
                                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 8px 16px rgba(59, 130, 246, 0.2)',
                                },
                                transition: 'all 0.3s ease-in-out',
                              }}
                            >
                              Export JSON
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<ShareIcon />}
                              onClick={() => {
                                const csvContent = [
                                  'Category,Tag',
                                  ...Object.entries(ocrResult.tagsResult?.categories || {}).flatMap(([category, tags]) =>
                                    (tags as string[]).map((tag: string) => `${category},${tag}`)
                                  ),
                                  `all,${ocrResult.tagsResult?.tags?.join(',') || ''}`
                                ].join('\n');
                                const blob = new Blob([csvContent], { type: 'text/csv' });
                                const url = URL.createObjectURL(blob);
                                const a = window.document.createElement('a');
                                a.href = url;
                                a.download = `image-tags-${document?.file_name?.split('.')[0] || 'image'}.csv`;
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success('Tags exported as CSV!');
                              }}
                              sx={{
                                borderColor: '#8b5cf6',
                                color: '#8b5cf6',
                                '&:hover': {
                                  borderColor: '#7c3aed',
                                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 8px 16px rgba(139, 92, 246, 0.2)',
                                },
                                transition: 'all 0.3s ease-in-out',
                              }}
                            >
                              Export CSV
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<SearchIcon />}
                              onClick={() => {
                                // Get all tags and navigate to products page with only tags
                                const allTags = ocrResult.tagsResult?.tags || [];
                                const tagsQuery = allTags.join(',');
                                
                                // Navigate with only tags parameter
                                if (tagsQuery.trim()) {
                                  navigate(`/all-products?tags=${encodeURIComponent(tagsQuery)}`);
                                } else {
                                  navigate('/all-products');
                                }
                                onClose(); // Close the modal after navigation
                              }}
                              sx={{
                                background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                                color: 'white',
                                fontWeight: 600,
                                px: 4,
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)',
                                border: 'none',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #d97706 0%, #ea580c 100%)',
                                  transform: 'translateY(-3px) scale(1.02)',
                                  boxShadow: '0 12px 40px rgba(245, 158, 11, 0.4), 0 4px 20px rgba(245, 158, 11, 0.2)',
                                },
                                '&:active': {
                                  transform: 'translateY(-1px) scale(1.01)',
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: '-100%',
                                  width: '100%',
                                  height: '100%',
                                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                                  transition: 'left 0.5s',
                                },
                                '&:hover::before': {
                                  left: '100%',
                                },
                              }}
                            >
                              Search Products with Tags
                            </Button>
                          </Box>
                        </motion.div>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {ocrResult.userMessage || ocrResult.error || 'An error occurred while processing the document.'}
                    </Typography>
                    {ocrResult.keysTried && (
                      <Typography variant="body2" color="text.secondary">
                        Tried {ocrResult.keysTried} API key(s) from your account.
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Summary Results */}
        <AnimatePresence>
          {summaryResult && selectedOption === 'summarize' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Paper
                sx={{
                  p: 4,
                  mb: 3,
                  borderRadius: 3,
                  background: summaryResult.success
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%)',
                  border: summaryResult.success
                    ? '1px solid rgba(16, 185, 129, 0.2)'
                    : '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  {summaryResult.success ? (
                    <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
                  ) : (
                    <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                  )}
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    {summaryResult.success ? 'Summary Generated Successfully!' : 'Summary Generation Failed'}
                  </Typography>
                </Box>
                
                {summaryResult.success ? (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {summaryResult.userMessage || 'Document summary generated successfully using Mistral AI.'}
                    </Typography>
                    
                    {/* Summary Statistics */}
                    {summaryResult.word_count && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${summaryResult.word_count} words`}
                          size="small"
                          sx={{ bgcolor: alpha('#10b981', 0.1), color: '#059669' }}
                        />
                        {summaryResult.original_length && (
                          <Chip
                            label={`${summaryResult.original_length} original words`}
                            size="small"
                            sx={{ bgcolor: alpha('#3b82f6', 0.1), color: '#1e40af' }}
                          />
                        )}
                        {summaryResult.compression_ratio && (
                          <Chip
                            label={`${summaryResult.compression_ratio}% compression`}
                            size="small"
                            sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#d97706' }}
                          />
                        )}
                        {summaryResult.model && (
                          <Chip
                            label={`Model: ${summaryResult.model}`}
                            size="small"
                            sx={{ bgcolor: alpha('#8b5cf6', 0.1), color: '#7c3aed' }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Context Information */}
                    {summaryResult.context && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <Chip
                          label={`Access: ${summaryResult.context.accessType === 'company' ? 'Company' : 'User'}`}
                          size="small"
                          sx={{ 
                            bgcolor: summaryResult.context.accessType === 'company' 
                              ? alpha('#f59e0b', 0.1) 
                              : alpha('#10b981', 0.1), 
                            color: summaryResult.context.accessType === 'company' 
                              ? '#d97706' 
                              : '#059669',
                            fontWeight: 'bold'
                          }}
                        />
                        {summaryResult.context.accessType === 'company' && summaryResult.context.companyName && (
                          <Chip
                            label={`Company: ${summaryResult.context.companyName}`}
                            size="small"
                            sx={{ 
                              bgcolor: alpha('#f59e0b', 0.1), 
                              color: '#d97706',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                        {summaryResult.context.accessType === 'company' && summaryResult.context.companyId && (
                          <Chip
                            label={`ID: ${summaryResult.context.companyId.substring(0, 8)}...`}
                            size="small"
                            sx={{ 
                              bgcolor: alpha('#f59e0b', 0.1), 
                              color: '#d97706',
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                        {summaryResult.context.userId && (
                          <Chip
                            label={`User: ${summaryResult.context.userId.substring(0, 8)}...`}
                            size="small"
                            sx={{ 
                              bgcolor: alpha('#6b7280', 0.1), 
                              color: '#6b7280',
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                        {summaryResult.context.hasApiKeys !== undefined && (
                          <Chip
                            label={`API Keys: ${summaryResult.context.apiKeyCount || 0} active`}
                            size="small"
                            sx={{ 
                              bgcolor: summaryResult.context.hasApiKeys 
                                ? alpha('#10b981', 0.1) 
                                : alpha('#ef4444', 0.1), 
                              color: summaryResult.context.hasApiKeys 
                                ? '#059669' 
                                : '#dc2626',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Summary Content */}
                    {summaryResult.summary && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#059669', fontSize: '1.1rem' }}>
                            📝 Document Summary:
                          </Typography>
                          {summaryResult.context && (
                            <Chip
                              label={`${summaryResult.context.accessType === 'company' ? `🏢 Company (${summaryResult.context.companyId?.substring(0, 8)}...)` : '👤 User'} Context`}
                              size="small"
                              sx={{ 
                                bgcolor: summaryResult.context.accessType === 'company' 
                                  ? alpha('#f59e0b', 0.2) 
                                  : alpha('#10b981', 0.2), 
                                color: summaryResult.context.accessType === 'company' 
                                  ? '#d97706' 
                                  : '#059669',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                        </Box>
                        
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
                            border: '2px solid rgba(16, 185, 129, 0.2)',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(59, 130, 246, 0.9) 100%)',
                              color: 'white',
                              px: 2,
                              py: 0.5,
                              borderRadius: '0 12px 0 12px',
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                          >
                            AI SUMMARY
                          </Box>
                          <Box
                            sx={{
                              lineHeight: 1.6,
                              color: '#374151',
                              wordBreak: 'break-word',
                              pr: 8, // Add padding to avoid overlap with badge
                              fontFamily: '"Times New Roman", Georgia, serif',
                              fontSize: '1rem',
                              textAlign: 'justify',
                              '& strong': {
                                fontWeight: 700,
                                color: '#1e293b',
                                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                padding: '2px 4px',
                                borderRadius: '4px',
                                border: '1px solid rgba(16, 185, 129, 0.2)',
                              }
                            }}
                            dangerouslySetInnerHTML={{
                              __html: summaryResult.summary
                                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
                                .replace(/\n/g, '<br/>')
                            }}
                          />
                        </Paper>
                      </Box>
                    )}

                    {/* Key Points */}
                    {summaryResult.key_points && summaryResult.key_points.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 3, color: '#059669', fontSize: '1.1rem' }}>
                          🔑 Key Points:
                        </Typography>
                        
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(251, 191, 36, 0.05) 100%)',
                            border: '2px solid rgba(245, 158, 11, 0.2)',
                          }}
                        >
                          <List sx={{ p: 0 }}>
                            {summaryResult.key_points.map((point: string, index: number) => (
                              <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: '#f59e0b',
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ lineHeight: 1.5, color: '#374151' }}>
                                      {point}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {summaryResult.userMessage || summaryResult.error || 'An error occurred while generating the summary.'}
                    </Typography>
                    {summaryResult.error && (
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          mt: 2,
                          bgcolor: 'rgba(239, 68, 68, 0.05)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#dc2626' }}>
                          Detailed Error:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7f1d1d' }}>
                          {summaryResult.error}
                        </Typography>
                        
                        {summaryResult.debugInfo && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#dc2626' }}>
                              Debug Information:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7f1d1d', fontSize: '0.875rem' }}>
                              {JSON.stringify(summaryResult.debugInfo, null, 2)}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    )}
                  </Box>
                )}
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Skill Analyze Results */}
        <AnimatePresence>
          {skillAnalyzeResult && selectedOption === 'skill-analyze' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Paper
                sx={{
                  p: 4,
                  mb: 3,
                  borderRadius: 3,
                  background: skillAnalyzeResult.success
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%)',
                  border: skillAnalyzeResult.success
                    ? '1px solid rgba(16, 185, 129, 0.2)'
                    : '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  {skillAnalyzeResult.success ? (
                    <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
                  ) : (
                    <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                  )}
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    {skillAnalyzeResult.success ? 'Skill Analysis Completed!' : 'Skill Analysis Failed'}
                  </Typography>
                </Box>
                
                {skillAnalyzeResult.success ? (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {skillAnalyzeResult.userMessage || 'Document skills analyzed successfully using Mistral AI.'}
                    </Typography>
                    
                    {/* Skill Analysis Statistics */}
                    {skillAnalyzeResult.skill_count && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${skillAnalyzeResult.skill_count} skills detected`}
                          size="small"
                          sx={{ bgcolor: alpha('#10b981', 0.1), color: '#059669' }}
                        />
                        {skillAnalyzeResult.original_length && (
                          <Chip
                            label={`${skillAnalyzeResult.original_length} characters analyzed`}
                            size="small"
                            sx={{ bgcolor: alpha('#3b82f6', 0.1), color: '#1e40af' }}
                          />
                        )}
                        {skillAnalyzeResult.model && (
                          <Chip
                            label={`Model: ${skillAnalyzeResult.model}`}
                            size="small"
                            sx={{ bgcolor: alpha('#8b5cf6', 0.1), color: '#7c3aed' }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Context Information */}
                    {skillAnalyzeResult.context && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <Chip
                          label={`Access: ${skillAnalyzeResult.context.accessType === 'company' ? 'Company' : 'User'}`}
                          size="small"
                          sx={{ 
                            bgcolor: skillAnalyzeResult.context.accessType === 'company' 
                              ? alpha('#f59e0b', 0.1) 
                              : alpha('#10b981', 0.1), 
                            color: skillAnalyzeResult.context.accessType === 'company' 
                              ? '#d97706' 
                              : '#059669',
                            fontWeight: 'bold'
                          }}
                        />
                        {skillAnalyzeResult.context.accessType === 'company' && skillAnalyzeResult.context.companyName && (
                          <Chip
                            label={`Company: ${skillAnalyzeResult.context.companyName}`}
                            size="small"
                            sx={{ 
                              bgcolor: alpha('#f59e0b', 0.1), 
                              color: '#d97706',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                        {skillAnalyzeResult.context.accessType === 'company' && skillAnalyzeResult.context.companyId && (
                          <Chip
                            label={`ID: ${skillAnalyzeResult.context.companyId.substring(0, 8)}...`}
                            size="small"
                            sx={{ 
                              bgcolor: alpha('#f59e0b', 0.1), 
                              color: '#d97706',
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                        {skillAnalyzeResult.context.userId && (
                          <Chip
                            label={`User: ${skillAnalyzeResult.context.userId.substring(0, 8)}...`}
                            size="small"
                            sx={{ 
                              bgcolor: alpha('#6b7280', 0.1), 
                              color: '#6b7280',
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                        {skillAnalyzeResult.context.hasApiKeys !== undefined && (
                          <Chip
                            label={`API Keys: ${skillAnalyzeResult.context.apiKeyCount || 0} active`}
                            size="small"
                            sx={{ 
                              bgcolor: skillAnalyzeResult.context.hasApiKeys 
                                ? alpha('#10b981', 0.1) 
                                : alpha('#ef4444', 0.1), 
                              color: skillAnalyzeResult.context.hasApiKeys 
                                ? '#059669' 
                                : '#dc2626',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Skills Display */}
                    {skillAnalyzeResult.skills && skillAnalyzeResult.skills.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#059669', fontSize: '1.1rem' }}>
                            🧠 Detected Skills:
                          </Typography>
                          {skillAnalyzeResult.context && (
                            <Chip
                              label={`${skillAnalyzeResult.context.accessType === 'company' ? `🏢 Company (${skillAnalyzeResult.context.companyId?.substring(0, 8)}...)` : '👤 User'} Context`}
                              size="small"
                              sx={{ 
                                bgcolor: skillAnalyzeResult.context.accessType === 'company' 
                                  ? alpha('#f59e0b', 0.2) 
                                  : alpha('#10b981', 0.2), 
                                color: skillAnalyzeResult.context.accessType === 'company' 
                                  ? '#d97706' 
                                  : '#059669',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                        </Box>
                        
                        {/* Categorized Skills */}
                        {(() => {
                          const categorizedSkills = categorizeSkills(skillAnalyzeResult.skills);
                          const categoryConfigs = [
                            {
                              key: 'education',
                              title: 'Education & Qualifications',
                              icon: <SchoolIcon />,
                              color: '#8b5cf6',
                              bgColor: alpha('#8b5cf6', 0.1),
                              borderColor: alpha('#8b5cf6', 0.3),
                              skills: categorizedSkills.education,
                              emptyMessage: 'No educational qualifications detected'
                            },
                            {
                              key: 'languages',
                              title: 'Languages',
                              icon: <LanguageIcon />,
                              color: '#06b6d4',
                              bgColor: alpha('#06b6d4', 0.1),
                              borderColor: alpha('#06b6d4', 0.3),
                              skills: categorizedSkills.languages,
                              emptyMessage: 'No language skills detected'
                            },
                            {
                              key: 'softSkills',
                              title: 'Soft Skills & Interpersonal',
                              icon: <SoftSkillsIcon />,
                              color: '#10b981',
                              bgColor: alpha('#10b981', 0.1),
                              borderColor: alpha('#10b981', 0.3),
                              skills: categorizedSkills.softSkills,
                              emptyMessage: 'No soft skills detected'
                            },
                            {
                              key: 'technicalSkills',
                              title: 'Technical & IT Skills',
                              icon: <CodeIcon />,
                              color: '#3b82f6',
                              bgColor: alpha('#3b82f6', 0.1),
                              borderColor: alpha('#3b82f6', 0.3),
                              skills: categorizedSkills.technicalSkills,
                              emptyMessage: 'No technical skills detected'
                            },
                            {
                              key: 'industrySkills',
                              title: 'Industry-Specific Skills',
                              icon: <WorkIcon />,
                              color: '#059669',
                              bgColor: alpha('#059669', 0.1),
                              borderColor: alpha('#059669', 0.3),
                              skills: categorizedSkills.industrySkills,
                              emptyMessage: 'No industry-specific skills detected'
                            },
                            {
                              key: 'softwareTools',
                              title: 'Software & Digital Tools',
                              icon: <AppsIcon />,
                              color: '#0ea5e9',
                              bgColor: alpha('#0ea5e9', 0.1),
                              borderColor: alpha('#0ea5e9', 0.3),
                              skills: categorizedSkills.softwareTools,
                              emptyMessage: 'No software tools detected'
                            },
                            {
                              key: 'salesMarketing',
                              title: 'Sales & Marketing',
                              icon: <AnalyticsIcon />,
                              color: '#f59e0b',
                              bgColor: alpha('#f59e0b', 0.1),
                              borderColor: alpha('#f59e0b', 0.3),
                              skills: categorizedSkills.salesMarketing,
                              emptyMessage: 'No sales or marketing skills detected'
                            },
                            {
                              key: 'healthcare',
                              title: 'Healthcare & Medical',
                              icon: <CheckIcon />,
                              color: '#ef4444',
                              bgColor: alpha('#ef4444', 0.1),
                              borderColor: alpha('#ef4444', 0.3),
                              skills: categorizedSkills.healthcare,
                              emptyMessage: 'No healthcare skills detected'
                            },
                            {
                              key: 'finance',
                              title: 'Finance & Accounting',
                              icon: <TableIcon />,
                              color: '#16a34a',
                              bgColor: alpha('#16a34a', 0.1),
                              borderColor: alpha('#16a34a', 0.3),
                              skills: categorizedSkills.finance,
                              emptyMessage: 'No finance or accounting skills detected'
                            },
                            {
                              key: 'educationTeaching',
                              title: 'Education & Teaching',
                              icon: <SchoolIcon />,
                              color: '#7c3aed',
                              bgColor: alpha('#7c3aed', 0.1),
                              borderColor: alpha('#7c3aed', 0.3),
                              skills: categorizedSkills.educationTeaching,
                              emptyMessage: 'No teaching or education skills detected'
                            },
                            {
                              key: 'legal',
                              title: 'Legal & Compliance',
                              icon: <BadgeIcon />,
                              color: '#dc2626',
                              bgColor: alpha('#dc2626', 0.1),
                              borderColor: alpha('#dc2626', 0.3),
                              skills: categorizedSkills.legal,
                              emptyMessage: 'No legal skills detected'
                            },
                            {
                              key: 'operations',
                              title: 'Operations & Administration',
                              icon: <BuildIcon />,
                              color: '#f97316',
                              bgColor: alpha('#f97316', 0.1),
                              borderColor: alpha('#f97316', 0.3),
                              skills: categorizedSkills.operations,
                              emptyMessage: 'No operational skills detected'
                            },
                            {
                              key: 'creative',
                              title: 'Creative & Arts',
                              icon: <ImageIcon />,
                              color: '#ec4899',
                              bgColor: alpha('#ec4899', 0.1),
                              borderColor: alpha('#ec4899', 0.3),
                              skills: categorizedSkills.creative,
                              emptyMessage: 'No creative skills detected'
                            },
                            {
                              key: 'certifications',
                              title: 'Professional Certifications',
                              icon: <BadgeIcon />,
                              color: '#6366f1',
                              bgColor: alpha('#6366f1', 0.1),
                              borderColor: alpha('#6366f1', 0.3),
                              skills: categorizedSkills.certifications,
                              emptyMessage: 'No professional certifications detected'
                            },
                            {
                              key: 'other',
                              title: 'Other Skills',
                              icon: <StarIcon />,
                              color: '#6b7280',
                              bgColor: alpha('#6b7280', 0.1),
                              borderColor: alpha('#6b7280', 0.3),
                              skills: categorizedSkills.other,
                              emptyMessage: 'No other skills detected'
                            }
                          ];

                          return categoryConfigs.map((category) => {
                            return (
                              <motion.div
                                key={category.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                              >
                                <Paper
                                  elevation={2}
                                  sx={{
                                    p: 3,
                                    mb: 2,
                                    borderRadius: 3,
                                    background: `linear-gradient(135deg, ${category.bgColor} 0%, ${alpha(category.color, 0.05)} 100%)`,
                                    border: `2px solid ${category.borderColor}`,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    opacity: category.skills.length === 0 ? 0.6 : 1,
                                  }}
                                >
                                  {/* Category Header */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      mb: 2,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: 1,
                                        borderRadius: 2,
                                        bgcolor: category.bgColor,
                                        color: category.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {category.icon}
                                    </Box>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 600,
                                        color: category.color,
                                        fontSize: '1.1rem',
                                      }}
                                    >
                                      {category.title}
                                    </Typography>
                                    <Chip
                                      label={`${category.skills.length} skill${category.skills.length !== 1 ? 's' : ''}`}
                                      size="small"
                                      sx={{
                                        bgcolor: category.bgColor,
                                        color: category.color,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        ml: 'auto',
                                      }}
                                    />
                                  </Box>

                                  {/* Skills Grid or Empty State */}
                                  {category.skills.length > 0 ? (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1.5,
                                      }}
                                    >
                                      {category.skills.map((skill: string, index: number) => (
                                        <motion.div
                                          key={index}
                                          initial={{ opacity: 0, scale: 0.8 }}
                                          animate={{ opacity: 1, scale: 1 }}
                                          transition={{ duration: 0.2, delay: index * 0.05 }}
                                        >
                                          <Chip
                                            label={skill}
                                            sx={{
                                              bgcolor: category.bgColor,
                                              color: category.color,
                                              border: `1px solid ${category.borderColor}`,
                                              fontWeight: 500,
                                              fontSize: '0.875rem',
                                              height: '36px',
                                              px: 2,
                                              '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 6px 12px ${alpha(category.color, 0.2)}`,
                                                bgcolor: alpha(category.color, 0.15),
                                              },
                                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            }}
                                          />
                                        </motion.div>
                                      ))}
                                    </Box>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        py: 3,
                                        px: 2,
                                        borderRadius: 2,
                                        bgcolor: alpha(category.color, 0.05),
                                        border: `1px dashed ${alpha(category.color, 0.3)}`,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: alpha(category.color, 0.7),
                                          fontStyle: 'italic',
                                          textAlign: 'center',
                                        }}
                                      >
                                        {category.emptyMessage}
                                      </Typography>
                                    </Box>
                                  )}
                                </Paper>
                              </motion.div>
                            );
                          });
                        })()}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {skillAnalyzeResult.userMessage || skillAnalyzeResult.error || 'An error occurred while analyzing skills.'}
                    </Typography>
                    {skillAnalyzeResult.error && (
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          mt: 2,
                          bgcolor: 'rgba(239, 68, 68, 0.05)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#dc2626' }}>
                          Detailed Error:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7f1d1d' }}>
                          {skillAnalyzeResult.error}
                        </Typography>
                        
                        {skillAnalyzeResult.debugInfo && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#dc2626' }}>
                              Debug Information:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7f1d1d', fontSize: '0.875rem' }}>
                              {JSON.stringify(skillAnalyzeResult.debugInfo, null, 2)}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    )}
                  </Box>
                )}
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Test Results */}
        <AnimatePresence>
          {testResult && selectedOption === 'test' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Paper
                sx={{
                  p: 4,
                  mb: 3,
                  borderRadius: 3,
                  background: testResult.success
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%)',
                  border: testResult.success
                    ? '1px solid rgba(16, 185, 129, 0.2)'
                    : '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  {testResult.success ? (
                    <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
                  ) : (
                    <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                  )}
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    {testResult.success ? 'Connection Test Successful!' : 'Connection Test Failed'}
                  </Typography>
                </Box>
                
                {testResult.success ? (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {testResult.message || 'Connection to edge function is working correctly!'}
                    </Typography>
                    
                    {/* Test Statistics */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                      <Chip
                        label={`Status: ${testResult.success ? 'Success' : 'Failed'}`}
                        size="small"
                        sx={{ bgcolor: alpha('#10b981', 0.1), color: '#059669' }}
                      />
                      {testResult.timestamp && (
                        <Chip
                          label={`Time: ${new Date(testResult.timestamp).toLocaleTimeString()}`}
                          size="small"
                          sx={{ bgcolor: alpha('#3b82f6', 0.1), color: '#1e40af' }}
                        />
                      )}
                      {testResult.requestMethod && (
                        <Chip
                          label={`Method: ${testResult.requestMethod}`}
                          size="small"
                          sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#d97706' }}
                        />
                      )}
                    </Box>

                    {/* Debug Information */}
                    {testResult.debugInfo && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 3, color: '#059669', fontSize: '1.1rem' }}>
                          🔧 Debug Information:
                        </Typography>
                        
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
                            border: '2px solid rgba(16, 185, 129, 0.2)',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              color: '#374151',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {JSON.stringify(testResult.debugInfo, null, 2)}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {testResult.userMessage || testResult.error || 'An error occurred while testing the edge function.'}
                    </Typography>
                    {testResult.error && (
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          mt: 2,
                          bgcolor: 'rgba(239, 68, 68, 0.05)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#dc2626' }}>
                          Detailed Error:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7f1d1d' }}>
                          {testResult.error}
                        </Typography>
                        
                        {testResult.debugInfo && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#dc2626' }}>
                              Debug Information:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7f1d1d', fontSize: '0.875rem' }}>
                              {JSON.stringify(testResult.debugInfo, null, 2)}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    )}
                  </Box>
                )}
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Processing State */}
        <AnimatePresence>
          {processing && selectedOption && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Image Scanning Animation */}
              {document && isImageDocument(document.file_type) ? (
                <Paper
                  sx={{
                    p: 0,
                    mb: 3,
                    borderRadius: 3,
                    overflow: 'hidden',
                    position: 'relative',
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                    border: '2px solid #0ea5e9',
                    boxShadow: '0 20px 40px rgba(14, 165, 233, 0.3)',
                  }}
                >
                  {/* Image Container */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      maxHeight: '70vh',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {/* Image */}
                    <img
                      src={document.url}
                      alt={document.file_name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        filter: 'brightness(0.7)',
                      }}
                    />

                    {/* Scanning Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(90deg, transparent 0%, rgba(14, 165, 233, 0.3) 50%, transparent 100%)',
                        animation: 'scanning 2s ease-in-out infinite',
                        '@keyframes scanning': {
                          '0%': {
                            transform: 'translateX(-100%)',
                          },
                          '100%': {
                            transform: 'translateX(100%)',
                          },
                        },
                        pointerEvents: 'none',
                      }}
                    />

                    {/* Scanning Lines */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `
                          repeating-linear-gradient(
                            0deg,
                            transparent,
                            transparent 2px,
                            rgba(14, 165, 233, 0.1) 2px,
                            rgba(14, 165, 233, 0.1) 4px
                          )
                        `,
                        animation: 'scanLines 0.5s linear infinite',
                        '@keyframes scanLines': {
                          '0%': {
                            transform: 'translateY(0)',
                          },
                          '100%': {
                            transform: 'translateY(8px)',
                          },
                        },
                        pointerEvents: 'none',
                      }}
                    />

                    {/* Pulsing Border */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -2,
                        left: -2,
                        right: -2,
                        bottom: -2,
                        border: '2px solid #0ea5e9',
                        borderRadius: 3,
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': {
                            opacity: 0.5,
                            transform: 'scale(1)',
                          },
                          '50%': {
                            opacity: 1,
                            transform: 'scale(1.02)',
                          },
                        },
                      }}
                    />

                    {/* Corner Indicators */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        width: 20,
                        height: 20,
                        borderTop: '3px solid #0ea5e9',
                        borderLeft: '3px solid #0ea5e9',
                        animation: 'cornerPulse 1s ease-in-out infinite',
                        '@keyframes cornerPulse': {
                          '0%, 100%': { opacity: 0.3 },
                          '50%': { opacity: 1 },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        width: 20,
                        height: 20,
                        borderTop: '3px solid #0ea5e9',
                        borderRight: '3px solid #0ea5e9',
                        animation: 'cornerPulse 1s ease-in-out infinite 0.2s',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: 16,
                        width: 20,
                        height: 20,
                        borderBottom: '3px solid #0ea5e9',
                        borderLeft: '3px solid #0ea5e9',
                        animation: 'cornerPulse 1s ease-in-out infinite 0.4s',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        right: 16,
                        width: 20,
                        height: 20,
                        borderBottom: '3px solid #0ea5e9',
                        borderRight: '3px solid #0ea5e9',
                        animation: 'cornerPulse 1s ease-in-out infinite 0.6s',
                      }}
                    />

                    {/* Center Scanning Circle */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 60,
                        height: 60,
                        border: '2px solid #0ea5e9',
                        borderRadius: '50%',
                        animation: 'centerScan 2s ease-in-out infinite',
                        '@keyframes centerScan': {
                          '0%, 100%': {
                            opacity: 0.3,
                            transform: 'translate(-50%, -50%) scale(0.8)',
                          },
                          '50%': {
                            opacity: 1,
                            transform: 'translate(-50%, -50%) scale(1.2)',
                          },
                        },
                      }}
                    />

                    {/* Scanning Text Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: 20,
                        right: 20,
                        background: 'rgba(0, 0, 0, 0.8)',
                        borderRadius: 2,
                        p: 2,
                        backdropFilter: 'blur(10px)',
                        pointerEvents: 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#0ea5e9',
                            animation: 'blink 1s ease-in-out infinite',
                            '@keyframes blink': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.3 },
                            },
                          }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                          🔍 Scanning Image...
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1 }}>
                        AI is analyzing visual elements and generating tags
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            flex: 1,
                            height: 4,
                            background: 'rgba(14, 165, 233, 0.2)',
                            borderRadius: 2,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              background: 'linear-gradient(90deg, #0ea5e9 0%, #06b6d4 100%)',
                              animation: 'progressScan 2s ease-in-out infinite',
                              '@keyframes progressScan': {
                                '0%': { width: '0%' },
                                '100%': { width: '100%' },
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: '#94a3b8', minWidth: '40px' }}>
                          {selectedOption === 'image-tags' ? 'Generating Tags' : 'Analyzing'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ) : (
                /* Regular Processing Animation for Non-Images */
                <Paper
                  sx={{
                    p: 4,
                    mb: 3,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                    border: '1px solid #0ea5e9',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: '#0ea5e9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AIIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0c4a6e' }}>
                      Processing Document...
                    </Typography>
                  </Box>
                  <LinearProgress
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha('#0ea5e9', 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: 'linear-gradient(90deg, #0ea5e9 0%, #06b6d4 100%)',
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#0369a1', mt: 1 }}>
                    AI is analyzing your document. This may take a few moments...
                  </Typography>
                </Paper>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* OCR Confirmation Dialog */}
        <AnimatePresence>
          {showOcrConfirmation && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Paper
                sx={{
                  p: 4,
                  mb: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  border: '2px solid #f59e0b',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AIIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#92400e' }}>
                      OCR Data Already Exists
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a16207' }}>
                      This document already has extracted text saved in the database.
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body1" sx={{ color: '#92400e', mb: 3, lineHeight: 1.6 }}>
                  Would you like to create a new AI analysis or view the existing extracted text?
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowOcrConfirmation(false)}
                    sx={{
                      borderColor: '#f59e0b',
                      color: '#92400e',
                      '&:hover': {
                        borderColor: '#d97706',
                        backgroundColor: alpha('#f59e0b', 0.1),
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleShowExistingOCR}
                    sx={{
                      backgroundColor: '#10b981',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#059669',
                      },
                    }}
                    startIcon={<DocumentIcon />}
                  >
                    View Existing Text
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleCreateNewAnalysis}
                    sx={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#2563eb',
                      },
                    }}
                    startIcon={<AIIcon />}
                  >
                    Create New Analysis
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* AI Options */}
        {!processing && !showOcrConfirmation && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {Object.entries(groupedOptions).map(([category, options]) => (
              <Box key={category}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 20,
                      borderRadius: 2,
                      bgcolor: CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS],
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  {options.map((option) => (
                    <motion.div
                      key={option.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Card
                        sx={{
                          cursor: option.comingSoon ? 'not-allowed' : 'pointer',
                          opacity: option.comingSoon ? 0.6 : 1,
                          border: option.id === 'write-ai-app' && isJobAppWriterMinimized 
                            ? '2px solid #8b5cf6' 
                            : '1px solid #e2e8f0',
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          backgroundColor: option.id === 'write-ai-app' && isJobAppWriterMinimized 
                            ? alpha('#8b5cf6', 0.05) 
                            : 'transparent',
                          '&:hover': {
                            borderColor: CATEGORY_COLORS[option.category],
                            boxShadow: `0 4px 12px ${alpha(CATEGORY_COLORS[option.category], 0.15)}`,
                            backgroundColor: option.id === 'write-ai-app' && isJobAppWriterMinimized 
                              ? alpha('#8b5cf6', 0.1) 
                              : 'transparent',
                          },
                        }}
                        onClick={() => !option.comingSoon && handleOptionSelect(option.id)}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: alpha(CATEGORY_COLORS[option.category], 0.1),
                                color: CATEGORY_COLORS[option.category],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 48,
                              }}
                            >
                              {option.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                  {option.title}
                                  {option.id === 'write-ai-app' && isJobAppWriterMinimized && (
                                    <span style={{ color: '#8b5cf6', marginLeft: '8px' }}>• Restore</span>
                                  )}
                                </Typography>
                                {option.premium && (
                                  <Chip
                                    label="Premium"
                                    size="small"
                                    sx={{
                                      bgcolor: '#f59e0b',
                                      color: 'white',
                                      fontSize: '0.75rem',
                                      height: 20,
                                    }}
                                  />
                                )}
                                {option.comingSoon && (
                                  <Chip
                                    label="Coming Soon"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderColor: '#64748b',
                                      color: '#64748b',
                                      fontSize: '0.75rem',
                                      height: 20,
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.5 }}>
                                {option.description}
                              </Typography>
                            </Box>
                            {!option.comingSoon && (
                              <Box
                                sx={{
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: alpha(CATEGORY_COLORS[option.category], 0.1),
                                  color: CATEGORY_COLORS[option.category],
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <ArrowIcon sx={{ fontSize: 20 }} />
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}

      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
          }}
        >
          Close
        </Button>
      </DialogActions>
      
      {/* Exercise Selection Modal */}
      <Dialog
        open={showExerciseSelection}
        onClose={() => setShowExerciseSelection(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'white',
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            boxShadow: '0 20px 60px rgba(236, 72, 153, 0.3), 0 8px 25px rgba(139, 92, 246, 0.2)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 2,
          px: 4,
          background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
          color: 'white',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            pointerEvents: 'none',
          }
        }}>
          {/* Close Button */}
          <IconButton
            onClick={() => setShowExerciseSelection(false)}
            sx={{
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Center Content */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 2,
            position: 'relative',
            zIndex: 1,
            flex: 1
          }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
            }}>
              <SchoolIcon sx={{ fontSize: 22, color: 'white' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
              Generate Learning Exercise
            </Typography>
          </Box>

          {/* Spacer for balance */}
          <Box sx={{ width: 48 }} />
        </DialogTitle>
        
        <DialogContent sx={{ 
          px: 4, 
          py: 4,
          pt: 6, // Add more top padding for spacing above tiles
          background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
          minHeight: '400px',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(236, 72, 153, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
          }
        }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: 3,
            position: 'relative',
            zIndex: 1
          }}>
            {/* Fill in the Blanks */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 3,
                background: 'white',
                border: '2px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'content-box, border-box',
                boxShadow: '0 4px 20px rgba(236, 72, 153, 0.1)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(236, 72, 153, 0.25), 0 4px 20px rgba(236, 72, 153, 0.15)',
                  backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
                }
              }}
              onClick={() => {
                setShowExerciseSelection(false);
                setShowFillGapsExercise(true);
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 8px 25px rgba(236, 72, 153, 0.3)',
                }}>
                  <Typography sx={{ fontSize: 28, fontWeight: 'bold', color: 'white' }}>
                    ___
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937', fontSize: '1.25rem' }}>
                  Fill in the Blanks
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  Create exercises where students fill in missing words or phrases from the document
                </Typography>
              </CardContent>
            </Card>

            {/* Matching Exercise */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 3,
                background: 'white',
                border: '2px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'content-box, border-box',
                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.1)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(245, 158, 11, 0.25), 0 4px 20px rgba(245, 158, 11, 0.15)',
                }
              }}
              onClick={() => {
                setShowExerciseSelection(false);
                setShowMatchingExercise(true);
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)',
                }}>
                  <LinkIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937', fontSize: '1.25rem' }}>
                  Matching Exercise
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  Connect related items with beautiful drag-and-drop curves
                </Typography>
              </CardContent>
            </Card>

            {/* Multiple Choice */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 3,
                background: 'white',
                border: '2px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'content-box, border-box',
                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.1)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(139, 92, 246, 0.25), 0 4px 20px rgba(139, 92, 246, 0.15)',
                }
              }}
              onClick={() => {
                setShowExerciseSelection(false);
                setShowMultipleChoiceExercise(true);
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 8px 25px rgba(139, 92, 246, 0.3)',
                }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 'bold', color: 'white' }}>
                    A B C D
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937', fontSize: '1.25rem' }}>
                  Multiple Choice Quiz
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  Generate questions with multiple answer options based on document content
                </Typography>
              </CardContent>
            </Card>

            {/* True/False */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 3,
                background: 'white',
                border: '2px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'content-box, border-box',
                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.1)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(245, 158, 11, 0.25), 0 4px 20px rgba(245, 158, 11, 0.15)',
                }
              }}
              onClick={() => {
                setShowExerciseSelection(false);
                // TODO: Implement true/false exercise generation
                console.log('Generate True/False exercise');
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)',
                }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 'bold', color: 'white' }}>
                    T F
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937', fontSize: '1.25rem' }}>
                  True/False Questions
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  Create statements that students must mark as true or false
                </Typography>
              </CardContent>
            </Card>

            {/* Word Search */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 3,
                background: 'white',
                border: '2px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'content-box, border-box',
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.1)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(59, 130, 246, 0.25), 0 4px 20px rgba(59, 130, 246, 0.15)',
                }
              }}
              onClick={() => {
                setShowExerciseSelection(false);
                // TODO: Implement word search exercise generation
                console.log('Generate Word Search exercise');
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
                }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: 'white' }}>
                    🔍
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937', fontSize: '1.25rem' }}>
                  Word Search Puzzle
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  Create a grid puzzle with key terms hidden in the document
                </Typography>
              </CardContent>
            </Card>

            {/* Crossword Puzzle */}
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 3,
                background: 'white',
                border: '2px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'content-box, border-box',
                boxShadow: '0 4px 20px rgba(239, 68, 68, 0.1)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 12px 40px rgba(239, 68, 68, 0.25), 0 4px 20px rgba(239, 68, 68, 0.15)',
                }
              }}
              onClick={() => {
                setShowExerciseSelection(false);
                // TODO: Implement crossword exercise generation
                console.log('Generate Crossword exercise');
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 8px 25px rgba(239, 68, 68, 0.3)',
                }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: 'white' }}>
                    #
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937', fontSize: '1.25rem' }}>
                  Crossword Puzzle
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  Generate crossword puzzles with clues based on document content
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 4, 
          pt: 2,
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderTop: '1px solid rgba(236, 72, 153, 0.1)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(236, 72, 153, 0.3) 50%, transparent 100%)',
          }
        }}>
          <Button
            onClick={() => setShowExerciseSelection(false)}
            variant="outlined"
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              borderColor: '#e2e8f0',
              color: '#64748b',
              background: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              '&:hover': {
                borderColor: '#cbd5e1',
                backgroundColor: '#f8fafc',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* AI Application Writer Modal */}
      <AIApplicationWriterModal
        open={showAIApplicationWriter && !isJobAppWriterMinimized}
        onClose={() => setShowAIApplicationWriter(false)}
        document={document || null}
        onMinimize={() => setIsJobAppWriterMinimized(true)}
        onRestore={() => setIsJobAppWriterMinimized(false)}
      />

      {/* Fill Gaps Exercise Modal */}
      <FillGapsExerciseModal
        open={showFillGapsExercise}
        onClose={() => setShowFillGapsExercise(false)}
        documentContent={existingOcr || summaryResult?.summary || ''}
      />

      {/* Matching Exercise Modal */}
      <MatchingExerciseModal
        open={showMatchingExercise}
        onClose={() => setShowMatchingExercise(false)}
        documentContent={existingOcr || summaryResult?.summary || ''}
      />

      {/* Multiple Choice Exercise Modal */}
      <MultipleChoiceExerciseModal
        open={showMultipleChoiceExercise}
        onClose={() => setShowMultipleChoiceExercise(false)}
        documentContent={existingOcr || summaryResult?.summary || ''}
      />
    </Dialog>
  );
};

export default AIDocumentProcessingModal;
